import thumbActions from '../../assets/review/thumbs/actions.jpg';
import thumbBackup from '../../assets/review/thumbs/backup.jpg';
import thumbDiscussion from '../../assets/review/thumbs/discussion.jpg';
import thumbFeedback from '../../assets/review/thumbs/feedback.jpg';
import thumbQuestions from '../../assets/review/thumbs/questions.jpg';
import thumbSecurity from '../../assets/review/thumbs/security.jpg';
import thumbService from '../../assets/review/thumbs/service.jpg';
import thumbSupport from '../../assets/review/thumbs/support.jpg';
import thumbThankYou from '../../assets/review/thumbs/thank-you.jpg';
import thumbTickets from '../../assets/review/thumbs/tickets.jpg';
import thumbWhatsNew from '../../assets/review/thumbs/whats-new.jpg';
import { Fragment, useEffect, useState } from 'react';
import {
  DeleteButton,
  Empty,
  ErrorBanner,
  Field,
  Loading,
  Modal,
  PageHead,
} from '../../components/ui';
import { api } from '../../lib/api';
import { readSpreadsheet } from '../../lib/spreadsheet';
import { matchAuditSheet, type Match } from './AmAuditSpreadsheet';
import {
  REC_STATUSES,
  blankCostLine,
  bucketTotals,
  costLinesOf,
  isOpenRec,
  priorityRank,
  topPriority,
} from '../../lib/amCosts';
import { formatCompactCurrency, formatDate, formatNumber, todayLocal } from '../../lib/format';
import { useAuthedImage, useHeldResource, useResource } from '../../lib/hooks';
import {
  imagePath,
  BACKUP_SLOTS,
  parseSlideImages,
  SERVICE_SLOTS,
  FEEDBACK_SLOTS,
  SLIDE_IMAGE_SLOTS,
  SUPPORT_SLIDE_SLOTS,
  type SavedSlideImage,
  type SlideImageSlot,
} from '../../lib/slideImages';
import {
  parseIdList,
  parseNumberIdList,
  type AmClientDetail,
  type AmComplianceSummary,
  type AmCostLine,
  type AmQuestion,
  type AmQuestionSet,
  type AmRecommendation,
  type AmRecommendationTemplate,
  type AmSbr,
  type AmStakeholder,
  type DeactivationChange,
} from '../../lib/types';
import { emptyActionItems, parseActionItems, type ActionItem } from '../../lib/actionItems';
import { emptyTakeaways, parseTakeaways } from '../../lib/supportTakeaways';
import {
  emptySupportSlide,
  freeSupportPair,
  MAX_SUPPORT_SLIDES,
  parseSupportSlides,
  type SupportSlide,
} from '../../lib/supportSlides';
import {
  DISCUSSION_COLUMNS,
  MAX_DISCUSSION_ITEMS,
  emptyDiscussion,
  parseDiscussion,
  type BusinessDiscussion,
} from '../../lib/businessDiscussion';
import {
  MAX_WHATS_NEW_LINES,
  emptyWhatsNew,
  parseWhatsNew,
  type WhatsNew,
  type WhatsNewKey,
} from '../../lib/whatsNew';
import { AgentHealthPreview, AmAgentHealth } from './AgentHealth';
import { AmClientHealth } from './AmClientHealth';
import { AmClientAssets } from './AmClientAssets';
import { AmServicePerformance } from './AmServicePerformance';
import { TEMPLATES_PATH, templateLines, templateProducts } from './AmRecTemplates';
import { AmWarranties } from './AmWarranties';
import { CsatPreview } from './CsatPanel';
import { ServicesPreview } from './ServicesProvided';
import { ProjectsPreview } from './ActiveProjects';
import { AssetsPreview } from './AssetSlides';
import { AmClientServices } from './AmClientServices';
import { AmContractRenewals } from './AmContractRenewals';
import { AmBudget, BudgetYearPicker } from './AmBudget';
import { AccountManagerPicker } from './AccountManagerPicker';
import { AmAuditEditor } from './AmAuditEditor';
import { AmAuditMatrix } from './AmAuditMatrix';
import { AmCompliance } from './AmCompliance';
import { AmProductMatrixCard } from './AmProductMatrix';
import {
  AmProductPicker,
  costLinesFromProducts,
  type PickedProduct,
} from './AmProductPicker';
import { AmProposal } from './AmProposal';
import { AmRiskAcceptance } from './AmRiskAcceptance';
import { AmSbrFollowup } from './AmSbrFollowup';
import { AmSbrPresentation, DEFAULT_CLOSING_WEBSITE } from './AmSbrPresentation';
import { AmClientSows } from './AmSow';
import {
  BucketTiles,
  CostCell,
  CostLinesEditor,
  HealthMeter,
  PriorityBadge,
  StateBadge,
  Tabs,
} from './amUi';

const CLIENT_STATUSES = ['Client', 'Prospect', 'At Risk', 'Inactive', 'Churned'] as const;
/* Ordered shortest interval to longest, so the two "bi-" labels read
   unambiguously from their neighbours: bi-monthly is every two months, and
   bi-annual is twice a year. Both are cadences clients actually ask for -
   quarterly is too often for a small account and too rare for a large one. */
const CADENCES = [
  'Weekly',
  'Monthly',
  'Bi-monthly',
  'Quarterly',
  'Bi-annual',
  'Annual',
] as const;

type Tab =
  | 'overview'
  | 'health'
  | 'stakeholders'
  | 'reviews'
  | 'audits'
  | 'recommendations'
  | 'products'
  | 'sows'
  | 'assets'
  | 'agenthealth'
  | 'service'
  | 'warranties'
  | 'services'
  | 'renewals'
  | 'budget'
  | 'compliance';

/** A deep view that replaces the whole workspace: an audit, a document, a deck. */
type View =
  | { kind: 'workspace' }
  | { kind: 'audit'; auditId: string }
  | { kind: 'riskForm'; auditId: string }
  | { kind: 'proposal' }
  | { kind: 'present'; sbrId: string; fullScreen?: boolean; print?: boolean }
  | { kind: 'followup'; sbrId: string };

/**
 * One client, end to end.
 *
 * Everything about the account is loaded in a single request and shared across
 * the tabs. The tabs are views over the same object rather than six endpoints,
 * because the numbers on them have to agree — a recommendation total that
 * differs between the overview and the recommendations tab is worse than no
 * total at all.
 */
export function AmClientWorkspace({
  clientId,
  organizationName,
  onBack,
  backToManager,
  onDeleted,
}: {
  clientId: string;
  organizationName: string;
  onBack: () => void;
  /** Back to the client list still filtered to the account manager it was
   *  filtered to — offered only when the list had a manager chosen. */
  backToManager?: { label: string; onClick: () => void };
  onDeleted: () => void;
}) {
  const resource = useResource<AmClientDetail>(`/am/clients/${clientId}`);
  /* Its own request rather than part of the client payload: it crosses into
     another module's tables, most clients have none, and the tab bar is the
     only thing that needs it before the tab is opened. A failure here must not
     take the whole client page down, so `useResource`'s error is simply left
     unread and the tab does not appear. */
  const compliance = useResource<AmComplianceSummary[]>(`/am/clients/${clientId}/compliance`);
  const complianceList = compliance.data ?? [];
  /* The Autotask Account Classification for the header, on the same terms as
     compliance above: its own request, because this one leaves the database
     entirely and goes to Autotask, and the header must not wait on it or fail
     with it. Held per tab, so moving between clients and back does not ask
     Autotask again. An unlinked client, a classification nobody set, or
     Autotask being down all arrive here as nothing, and the header leaves the
     line out. */
  const classificationRes = useHeldResource<AutotaskClassification>(
    `/am/clients/${clientId}/autotask/classification`,
  );
  const classification = classificationRes.data?.classification ?? null;
  const [tab, setTab] = useState<Tab>('overview');
  const [view, setView] = useState<View>({ kind: 'workspace' });
  const [editing, setEditing] = useState(false);
  /* The CS audit matrix, opened from the header. A modal rather than a tab:
     it is a window onto another module, not another face of this client. */
  const [auditMatrix, setAuditMatrix] = useState(false);
  const [deactivating, setDeactivating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (resource.loading) return <Loading label="Loading the client" />;
  if (resource.error) return <ErrorBanner message={resource.error} />;
  const client = resource.data;
  if (!client) return null;

  const backToWorkspace = () => setView({ kind: 'workspace' });
  const sbrById = (id: string) => client.sbrs.find((s) => s.AmSbrId === id);

  if (view.kind === 'audit') {
    return (
      <AmAuditEditor
        auditId={view.auditId}
        onBack={() => {
          backToWorkspace();
          resource.reload();
        }}
        onRiskForm={(auditId) => setView({ kind: 'riskForm', auditId })}
      />
    );
  }
  if (view.kind === 'riskForm') {
    return (
      <AmRiskAcceptance
        auditId={view.auditId}
        organizationName={organizationName}
        onBack={() => setView({ kind: 'audit', auditId: view.auditId })}
      />
    );
  }
  if (view.kind === 'proposal') {
    return <AmProposal client={client} organizationName={organizationName} onBack={backToWorkspace} />;
  }
  if (view.kind === 'present') {
    const sbr = sbrById(view.sbrId);
    if (sbr) {
      return (
        <AmSbrPresentation
          client={client}
          sbr={sbr}
          fullScreen={view.fullScreen}
          printAll={view.print}
          /* The print copy is opened from the follow-up, and goes back there. */
          onExit={view.print ? () => setView({ kind: 'followup', sbrId: view.sbrId }) : backToWorkspace}
          onFinished={() => {
            resource.reload();
            setView({ kind: 'followup', sbrId: view.sbrId });
          }}
        />
      );
    }
  }
  if (view.kind === 'followup') {
    const sbr = sbrById(view.sbrId);
    if (sbr) {
      return (
        <AmSbrFollowup
          client={client}
          sbr={sbr}
          organizationName={organizationName}
          onBack={backToWorkspace}
          onPresentationCopy={() => setView({ kind: 'present', sbrId: view.sbrId, print: true })}
        />
      );
    }
  }

  async function deleteClient() {
    try {
      await api.del(`/am/clients/${clientId}`);
      onDeleted();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not delete the client');
    }
  }

  const openRecs = client.recommendations.filter((r) => isOpenRec(r.Status));

  return (
    <>
      <div className="row wrap mb-4" style={{ gap: 'var(--space-2)' }}>
        <button type="button" className="btn btn-ghost btn-sm" onClick={onBack}>
          ← All clients
        </button>
        {backToManager && (
          <button type="button" className="btn btn-ghost btn-sm" onClick={backToManager.onClick}>
            ← Return to {backToManager.label}
          </button>
        )}
      </div>

      {error && <ErrorBanner message={error} />}

      <PageHead
        title={client.Name}
        lede={
          /* The classification leads: it says what KIND of account this is,
             which is the thing somebody reads the header for. It arrives
             after the first paint and drops in when Autotask answers. */
          [
            classification,
            client.Industry,
            client.ContractType,
            client.AccountManager && `Managed by ${client.AccountManager}`,
          ]
            .filter(Boolean)
            .join(' · ') || undefined
        }
        actions={
          <>
            <StateBadge status={client.Status} />
            {/* The audits themselves live in CS. This opens the client's matrix,
                and offers to build one when there is none — see AmAuditMatrix. */}
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => setAuditMatrix(true)}
            >
              Audit Matrix
            </button>
            {/* Microsoft Loop, where the account's working pages are kept. A new tab. */}
            <a className="btn btn-secondary btn-sm" href="https://loop.cloud.microsoft/" target="_blank" rel="noopener noreferrer">
              Open Loop ↗
            </a>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => setEditing(true)}>
              Edit
            </button>
            {/* Deactivating is the reversible one and comes first, because it
                is nearly always what somebody actually wants: the client has
                left, not never existed. */}
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => setDeactivating(true)}
            >
              Deactivate
            </button>
            <DeleteButton
              what={`${client.Name} and everything recorded against it`}
              onConfirm={() => void deleteClient()}
            />
          </>
        }
      />

      {/* The same navy band the account-management landing page uses, so
          opening a client is a change of scope rather than a change of
          language. */}
      <div className="am-hero mb-4">
        <div className="am-hero-stat">
          <div className="label">Monthly revenue</div>
          <div className="figure">{formatCompactCurrency(client.MonthlyRevenue)}</div>
          <div className="foot">
            {formatNumber((client.MonthlyRevenue ?? 0) * 12, 'currency')} a year
          </div>
        </div>
        <div className="am-hero-stat">
          <div className="label">Health</div>
          <div className={`figure${(client.HealthScore ?? 100) < 60 ? ' flagged' : ''}`}>
            {client.HealthScore ?? '—'}
          </div>
          <div className="foot">out of 100</div>
        </div>
        <div className="am-hero-stat">
          <div className="label">Open recommendations</div>
          <div className="figure">{openRecs.length}</div>
          <div className="foot">
            {formatNumber(bucketTotals(openRecs).oneTime, 'currency')} one-time in play
          </div>
        </div>
        <div className="am-hero-stat">
          <div className="label">Next review</div>
          <div className="figure sm">
            {client.sbrs.length === 0 ? 'None booked' : formatDate(client.sbrs[0]?.NextDate ?? null)}
          </div>
          <div className="foot">{client.sbrs[0]?.Cadence ?? 'nothing scheduled'}</div>
        </div>
      </div>

      <Tabs<Tab>
        active={tab}
        onChange={setTab}
        tabs={[
          /* Glyphs from the sidebar's own vocabulary, so they render in the
             same font at the same weight — no icon set imported for eight
             characters. Decorative only: the label says what each one is. */
          { key: 'overview', label: 'Overview', icon: '▚' },
          /* The calculated score and the sixteen measures behind it. */
          { key: 'health', label: 'Health', icon: '♥' },
          { key: 'stakeholders', label: 'Stakeholders', count: client.stakeholders.length, icon: '☺' },
          { key: 'reviews', label: 'Reviews', count: client.sbrs.length, icon: '▤' },
          { key: 'audits', label: 'Audits', count: client.audits.length, icon: '◫' },
          {
            key: 'recommendations',
            label: 'Recommendations',
            count: client.recommendations.length,
            icon: '◇',
          },
          { key: 'products', label: 'Products', count: client.productCategories.length, icon: '☷' },
          /* No count. It would need its own request before the tab is opened,
             and unlike Compliance this tab is worth showing empty: "nothing
             scoped for them yet" is the answer somebody came to check, and it
             is where they start the first one. */
          { key: 'sows', label: 'Scopes of work', icon: '§' },
          /* The client's configuration items, live from Autotask. No count for
             the same reason as Scopes of work: it would cost a call to
             Autotask before anybody opened the tab. */
          { key: 'assets', label: 'Assets', icon: '▣' },
          /* The client's agents, live from Automate — what is checking in,
             patched and protected. Beside Assets because they answer the two
             halves of one question: what the client has, and whether it is
             well. No count, for the same reason as Assets. */
          { key: 'agenthealth', label: 'Agent health', icon: '◉' },
          /* Server warranties, contract, firewall and VMware renewals as dials —
             live from Autotask, so no count either. */
          { key: 'warranties', label: 'Warranties', icon: '◔' },
          /* The services on the client's active Autotask contracts — no count, for the same reason. */
          { key: 'services', label: 'Services', icon: '☰' },
          /* Open tickets, SLA, resolution time, CSAT and the ticket flow, live
             from Autotask — no count, for the same reason as Assets. */
          { key: 'service', label: 'Service performance', icon: '◷' },
          /* Contracts, a year of billed labor, and a pricing workbench for the renewal. */
          { key: 'renewals', label: 'Contract renewals', icon: '↻' },
          /* Assets by lifecycle and the contract services, as a five-year plan. */
          { key: 'budget', label: 'Budget', icon: '$' },
          /* Only when the client actually has one. A Compliance tab that opens
             on "nothing here" for the nine clients out of ten who are not being
             assessed is a tab that teaches people not to click it. */
          ...(complianceList.length > 0
            ? [
                {
                  key: 'compliance' as Tab,
                  label: 'Compliance',
                  count: complianceList.length,
                  icon: '◎',
                },
              ]
            : []),
        ]}
      />

      {tab === 'overview' && (
        <OverviewTab
          client={client}
          onGoTo={setTab}
          onOpenAudit={(id) => setView({ kind: 'audit', auditId: id })}
        />
      )}
      {tab === 'health' && <AmClientHealth clientId={clientId} onChanged={resource.reload} />}
      {tab === 'stakeholders' && <StakeholdersTab client={client} reload={resource.reload} />}
      {tab === 'reviews' && (
        <ReviewsTab
          client={client}
          compliance={complianceList}
          reload={resource.reload}
          onPresent={(sbrId, fullScreen) => setView({ kind: 'present', sbrId, fullScreen })}
          onFollowup={(sbrId) => setView({ kind: 'followup', sbrId })}
        />
      )}
      {tab === 'audits' && (
        <AuditsTab
          client={client}
          reload={resource.reload}
          onOpen={(auditId) => setView({ kind: 'audit', auditId })}
        />
      )}
      {tab === 'recommendations' && (
        <RecommendationsTab
          client={client}
          reload={resource.reload}
          onProposal={() => setView({ kind: 'proposal' })}
        />
      )}
      {tab === 'products' && (
        <AmProductMatrixCard clientId={clientId} initial={client.productCategories} />
      )}
      {tab === 'sows' && (
        <AmClientSows clientId={clientId} organizationName={organizationName} />
      )}
      {tab === 'assets' && <AmClientAssets clientId={clientId} clientName={client.Name} />}
      {tab === 'agenthealth' && <AmAgentHealth clientId={clientId} clientName={client.Name} />}
      {tab === 'warranties' && <AmWarranties clientId={clientId} clientName={client.Name} />}
      {tab === 'services' && (
        <AmClientServices
          clientId={clientId}
          clientName={client.Name}
          monthlyRevenue={client.MonthlyRevenue}
          onMonthlyRevenue={resource.reload}
        />
      )}
      {tab === 'service' && <AmServicePerformance clientId={clientId} clientName={client.Name} />}
      {tab === 'renewals' && <AmContractRenewals clientId={clientId} clientName={client.Name} />}
      {tab === 'budget' && <AmBudget
          clientId={clientId}
          clientName={client.Name}
          organizationName={organizationName}
          recommendations={client.recommendations}
          reload={resource.reload}
        />}
      {tab === 'compliance' && (
        <AmCompliance clientId={clientId} assessments={complianceList} />
      )}

      {auditMatrix && (
        <AmAuditMatrix
          amClientId={clientId}
          clientName={client.Name}
          onClose={() => setAuditMatrix(false)}
        />
      )}

      {deactivating && (
        <DeactivateClientModal
          clientId={clientId}
          name={client.Name}
          onClose={() => setDeactivating(false)}
          onDone={() => {
            setDeactivating(false);
            onDeleted();
          }}
        />
      )}

      {editing && (
        <EditClientModal
          client={client}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            resource.reload();
          }}
        />
      )}
    </>
  );
}

/* =========================================================================
   Overview
   ========================================================================= */

function OverviewTab({
  client,
  onGoTo,
  onOpenAudit,
}: {
  client: AmClientDetail;
  onGoTo: (tab: Tab) => void;
  onOpenAudit: (auditId: string) => void;
}) {
  const latest = client.audits[0];
  /* Phone, website and address came off this card: they are the company's
     switchboard details rather than anything about the account, they are on
     the client form where they are edited, and they pushed the things somebody
     opens this card FOR — who owns it, who to ring, what they are on — below
     the fold. */
  const primaryContact =
    client.stakeholders.find((s) => s.IsPrimary)?.Name ?? client.PrimaryContact;
  const openRecs = [...client.recommendations]
    .filter((r) => isOpenRec(r.Status))
    .sort((a, b) => priorityRank(topPriority(b)) - priorityRank(topPriority(a)));
  /**
   * What is on the books, split by where it sits relative to today.
   *
   * client.sbrs arrives ordered by NextDate, so these keep that order and the
   * card reads down the calendar.
   *
   * OVERDUE STAYS ON THE CARD even though the ask was for the future ones.
   * The earliest review used to be the only thing shown, which meant an
   * overdue one WAS what you saw; listing only future dates would have made
   * the single most important row on this page disappear, and this app's whole
   * argument is that a review nobody has had is the thing to look at.
   *
   * `todayLocal`, not an ISO timestamp: business dates in this database
   * resolve through core.BusinessToday() in Mountain Time, and a UTC "today"
   * reads as tomorrow here every evening after five — which would file a
   * review booked for today under overdue.
   */
  const today = todayLocal();
  /* A completed review (166) was held: it is neither overdue nor ahead. */
  const open = client.sbrs.filter((s) => !s.CompletedUtc);
  const completedReviews = client.sbrs.length - open.length;
  const dated = open.filter((s) => s.NextDate);
  const overdueReviews = dated.filter((s) => (s.NextDate as string).slice(0, 10) < today);
  const upcomingReviews = dated.filter((s) => (s.NextDate as string).slice(0, 10) >= today);
  /* Booked with no date at all. Rare, and it is a real state — somebody has
     agreed a cadence and not picked the day — so it is counted rather than
     silently dropped from a card whose job is to say what is on the books. */
  const undatedReviews = open.length - dated.length;

  return (
    /* One column, full width. Two columns put every card at half the page and
       then wrapped the things that need the room — the recommendation table,
       the facts grid, the notes somebody actually types into — while leaving
       the short cards half empty. A client dashboard is read down the page,
       not in quadrants. */
    <div className="stack">
      <section className="card">
        <div className="card-head">
          <div className="grow">
            <span className="eyebrow">The account</span>
            <h2>Who and what</h2>
          </div>
        </div>
        <div className="card-body">
          <HealthMeter score={client.HealthScore} />
          <div className="am-facts mt-4">
            <Fact label="Account manager" value={client.AccountManager} />
            {/* The primary STAKEHOLDER's name, not the client column, so
                ticking "primary contact" on somebody in the Stakeholders tab
                shows here on the next read rather than waiting for the account
                form to be edited to match. The column is kept in step by the
                API and is the fallback for an account whose primary contact
                has never been made a stakeholder. */}
            <Fact label="Primary contact" value={primaryContact} />
            <Fact label="Email" value={client.Email} />
            <Fact label="Contract" value={client.ContractType} />
            {/* Imported spreadsheet columns. Shown as-is because the whole
                point is that this app does not know what they mean. */}
            {Object.entries(client.CustomFields).map(([label, value]) => (
              <Fact key={label} label={label} value={value} />
            ))}
          </div>
          {client.Notes && <p className="small muted mt-4">{client.Notes}</p>}
        </div>
      </section>

      <section className="card">
        <div className="card-head">
          <div className="grow">
            <span className="eyebrow">Cadence</span>
            <h2>Reviews booked</h2>
            {client.sbrs.length > 0 && (
              <p className="sub">
                {upcomingReviews.length === 0
                  ? 'Nothing ahead.'
                  : `${upcomingReviews.length} ${
                      upcomingReviews.length === 1 ? 'review' : 'reviews'
                    } still to come.`}
                {overdueReviews.length > 0 &&
                  ` ${overdueReviews.length} ${
                    overdueReviews.length === 1 ? 'is' : 'are'
                  } past due.`}
                {undatedReviews > 0 && ` ${undatedReviews} with no date set.`}
                {completedReviews > 0 && ` ${completedReviews} completed.`}
              </p>
            )}
          </div>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => onGoTo('reviews')}>
            Schedule
          </button>
        </div>
        <div className="card-body">
          {client.sbrs.length === 0 ? (
            <Empty title="Nothing booked">
              <p>
                This client never shows as overdue, because there is nothing to be overdue. That is
                the gap worth closing.
              </p>
            </Empty>
          ) : (
            <div className="stack">
              {/* Past due first. They are the ones somebody has to do something
                  about, and burying them under next quarter's date would be the
                  opposite of what this card is for. */}
              {overdueReviews.map((sbr) => (
                <ReviewLine key={sbr.AmSbrId} sbr={sbr} overdue />
              ))}
              {upcomingReviews.map((sbr) => (
                <ReviewLine key={sbr.AmSbrId} sbr={sbr} />
              ))}
              {/* Only a count, not rows. Without a date there is nothing to
                  sort them by or read off, and the Reviews tab is where one
                  gets a date. */}
              {undatedReviews > 0 && (
                <p className="xs muted">
                  {undatedReviews} {undatedReviews === 1 ? 'review has' : 'reviews have'} no date
                  yet — give {undatedReviews === 1 ? 'it' : 'them'} one on the Reviews tab.
                </p>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Directly under the schedule: what was said at the reviews that have notes. */}
      <ReviewNotesLinks client={client} />

      <section className="card">
        <div className="card-head">
          <div className="grow">
            <span className="eyebrow">What it turned into</span>
            <h2>Priority recommendations</h2>
          </div>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => onGoTo('recommendations')}
          >
            Manage
          </button>
        </div>
        {openRecs.length === 0 ? (
          <div className="card-body">
            <p className="small muted">Nothing open. Every recommendation is settled.</p>
          </div>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Recommendation</th>
                  <th style={{ width: 120 }}>Priority</th>
                  <th className="num" style={{ width: 140 }}>Price</th>
                </tr>
              </thead>
              <tbody>
                {openRecs.slice(0, 6).map((r) => (
                  <tr key={r.AmRecommendationId}>
                    <td>
                      <div className="strong">{r.Title}</div>
                      <div className="xs muted">{r.Status}</div>
                    </td>
                    <td>
                      <PriorityBadge priority={topPriority(r)} />
                    </td>
                    <td className="num">
                      <CostCell rec={r} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="card">
        <div className="card-head">
          <div className="grow">
            <span className="eyebrow">Environment</span>
            <h2>Latest audit</h2>
          </div>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => onGoTo('audits')}>
            All audits
          </button>
        </div>
        <div className="card-body">
          {!latest ? (
            <Empty title="No audit yet">
              <p>Nothing has been assessed. Run one from the audits tab.</p>
            </Empty>
          ) : (
            <>
              <div className="row-between wrap">
                <div>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => onOpenAudit(latest.AmAuditId)}
                  >
                    {latest.Name ?? 'Audit'}
                  </button>
                  <div className="xs muted mono">{formatDate(latest.AuditDate)}</div>
                </div>
                <StateBadge status={latest.Status} />
              </div>
              {/* Four nested stat cards for what is one distribution made the
                  shape of the risk impossible to see. A single bar shows the
                  balance at a glance; the counts are still there underneath
                  for anyone who needs the number. */}
              <RiskBar
                counts={{
                  Critical: latest.CriticalCount,
                  High: latest.HighCount,
                  Medium: latest.MediumCount,
                  Low: latest.LowCount,
                }}
              />

              <div className="mt-4">
                <div className="row-between">
                  <span className="xs muted">Answered</span>
                  <span className="xs muted">
                    {latest.AnswerCount} of {latest.QuestionCount}
                  </span>
                </div>
                <div className="meter mt-2" aria-hidden="true">
                  <span
                    className={progressOf(latest) < 50 ? 'low' : ''}
                    style={{ width: `${progressOf(latest)}%` }}
                  />
                </div>
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
}

/**
 * What was said at one review — its notes, kept on that review
 * (`coo.AmSbr.Notes`). Opened from the review's own row on the Reviews tab,
 * so pasted minutes land on the review they belong to, not on the client's
 * main page.
 *
 * Saved explicitly rather than on blur. These are minutes: half a sentence
 * autosaved mid-thought, then navigated away from, is worse than an unsaved
 * warning — so the button says what it is going to do.
 */
function ReviewNotesModal({
  sbr,
  clientName,
  onClose,
  onSaved,
}: {
  sbr: AmSbr;
  clientName: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [draft, setDraft] = useState<string>(sbr.Notes ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dirty = draft !== (sbr.Notes ?? '');

  const close = () => {
    if (dirty && !window.confirm('Close without saving these notes?')) return;
    onClose();
  };
  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.put(`/am/sbrs/${sbr.AmSbrId}`, { notes: draft });
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the notes');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={`Notes — ${sbr.Title ?? 'Review'}`}
      onClose={close}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={close}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" disabled={saving || !dirty} onClick={() => void save()}>
            {saving ? 'Saving…' : 'Save notes'}
          </button>
        </>
      }
    >
      {error && <ErrorBanner message={error} />}
      <p className="xs muted mb-2">
        Kept on this review — {sbr.Title ?? 'Review'}, {formatDate(sbr.NextDate)} — so it is there at the next one.
      </p>
      <textarea
        className="textarea"
        rows={16}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder={`Notes from the ${(sbr.Cadence ?? 'review').toLowerCase()} review with ${clientName}.\n\nWhat they raised, what we committed to, and who owns it.`}
        aria-label={`Notes for ${sbr.Title ?? 'the review'}`}
        autoFocus
      />
    </Modal>
  );
}

/**
 * The client's main page: a link to the notes of each review that has some,
 * by the review's name and date, opening straight to the notes. Read only — notes
 * are added and edited on the review itself (the Reviews tab's Notes).
 * Left off the page when no review has notes.
 */
function ReviewNotesLinks({ client }: { client: AmClientDetail }) {
  const withNotes = client.sbrs
    .filter((r) => r.Notes?.trim())
    .sort((a, b) => String(b.NextDate ?? '').localeCompare(String(a.NextDate ?? '')));
  const [open, setOpen] = useState<AmSbr | null>(null);
  if (withNotes.length === 0) return null;
  return (
    <section className="card">
      <div className="card-head">
        <div className="grow">
          <span className="eyebrow">After the meeting</span>
          <h2>Review notes</h2>
          <p className="sub">The notes kept on each review. Added and edited on the review, on the Reviews tab.</p>
        </div>
      </div>
      <div className="card-body">
        <ul className="am-notes-links">
          {withNotes.map((r) => (
            <li key={r.AmSbrId}>
              <button type="button" className="link-button" onClick={() => setOpen(r)}>
                {r.Title ?? 'Review'}
              </button>
              <span className="xs muted"> · {formatDate(r.NextDate)}</span>
            </li>
          ))}
        </ul>
      </div>
      {open && (
        <Modal
          title={`Notes — ${open.Title ?? 'Review'}`}
          onClose={() => setOpen(null)}
          footer={
            <button type="button" className="btn btn-primary" onClick={() => setOpen(null)}>
              Close
            </button>
          }
        >
          <p className="xs muted mb-2">
            {open.Title ?? 'Review'} · {formatDate(open.NextDate)}
          </p>
          <div className="am-notes-read">{open.Notes}</div>
        </Modal>
      )}
    </section>
  );
}

/** How far through its question set an audit has got. */
function progressOf(audit: { AnswerCount: number; QuestionCount: number }): number {
  if (!audit.QuestionCount) return 0;
  return Math.min(100, Math.round((audit.AnswerCount / audit.QuestionCount) * 100));
}

const RISK_ORDER = ['Critical', 'High', 'Medium', 'Low'] as const;

/**
 * The risk distribution of an audit, as one bar.
 *
 * Segments use the status ladder tokens rather than fixed colours, so they
 * follow the brand (navy is fine, crimson climbs) and pick up the dark-theme
 * values for free. An audit with nothing rated says so instead of rendering an
 * empty bar that reads as "no risk".
 */
function RiskBar({ counts }: { counts: Record<(typeof RISK_ORDER)[number], number> }) {
  const total = RISK_ORDER.reduce((sum, key) => sum + counts[key], 0);

  if (total === 0) {
    return <p className="small muted mt-4">Nothing has been rated on this audit yet.</p>;
  }

  return (
    <div className="mt-4">
      <div
        className="am-risk"
        role="img"
        aria-label={RISK_ORDER.filter((k) => counts[k] > 0)
          .map((k) => `${counts[k]} ${k.toLowerCase()}`)
          .join(', ')}
      >
        {RISK_ORDER.map((key) =>
          counts[key] > 0 ? (
            <span
              key={key}
              className={`am-risk-seg ${key.toLowerCase()}`}
              style={{ flex: counts[key] }}
            />
          ) : null,
        )}
      </div>
      <div className="row wrap mt-2" style={{ gap: 'var(--space-3)' }}>
        {RISK_ORDER.map((key) => (
          <span key={key} className={`xs ${counts[key] > 0 ? 'muted' : 'subtle'}`}>
            <span className={`am-key ${key.toLowerCase()}`} /> {counts[key]} {key.toLowerCase()}
          </span>
        ))}
      </div>
    </div>
  );
}

function Fact({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div>
      <div className="am-fact-label">{label}</div>
      <div className="small">{value?.trim() || '—'}</div>
    </div>
  );
}

/* =========================================================================
   Stakeholders
   ========================================================================= */

const RELATIONSHIP_FIELDS = [
  ['Birthday', 'Birthday'],
  ['Spouse', 'Spouse or partner'],
  ['Kids', 'Kids'],
  ['Interests', 'Interests'],
  ['LifeEvents', 'Life events'],
  ['Notes', 'Notes'],
] as const;

/** A birthday without the year: knowing the date is useful, knowing someone's
 *  age is nobody's business. */
function formatBirthday(value: string | null): string {
  if (!value) return '';
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!match) return value;
  const [, y, m, d] = match as unknown as [string, string, string, string];
  return new Date(Number(y), Number(m) - 1, Number(d)).toLocaleDateString(undefined, {
    month: 'long',
    day: 'numeric',
  });
}

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
/* 29 days for February: the stored year is 2000, a leap year, so a 29 February birthday has somewhere to live. */
const DAYS_IN = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

/**
 * A birthday as a month and a day, no year. The column is a DATE, so a new
 * birthday is kept as that day in 2000 (a leap year); one already on record
 * keeps whatever year it has. The year is never shown anywhere.
 */
function BirthdayPicker({ value, onChange }: { value: string | null; onChange: (v: string | null) => void }) {
  const match = value ? /^(\d{4})-(\d{2})-(\d{2})/.exec(value) : null;
  const year = match?.[1] ?? '2000';
  const [month, setMonth] = useState(match ? Number(match[2]) : 0);
  const [dayOf, setDayOf] = useState(match ? Number(match[3]) : 0);
  const save = (m: number, d: number) => {
    setMonth(m);
    setDayOf(d);
    if (!m) onChange(null);
    else if (d) onChange(`${year}-${String(m).padStart(2, '0')}-${String(Math.min(d, DAYS_IN[m - 1]!)).padStart(2, '0')}`);
  };
  return (
    <div className="row" style={{ gap: 'var(--space-2)' }}>
      <select className="select" aria-label="Birthday month" value={month} onChange={(e) => save(Number(e.target.value), Number(e.target.value) ? dayOf : 0)}>
        <option value={0}>Month</option>
        {MONTHS.map((name, i) => (
          <option key={name} value={i + 1}>
            {name}
          </option>
        ))}
      </select>
      <select className="select" aria-label="Birthday day" value={dayOf} disabled={!month} onChange={(e) => save(month, Number(e.target.value))}>
        <option value={0}>Day</option>
        {Array.from({ length: month ? DAYS_IN[month - 1]! : 31 }, (_, i) => (
          <option key={i + 1} value={i + 1}>
            {i + 1}
          </option>
        ))}
      </select>
    </div>
  );
}

const hasRelationshipDetail = (s: AmStakeholder): boolean =>
  RELATIONSHIP_FIELDS.some(([key]) => (s[key] ?? '').toString().trim());

function StakeholdersTab({
  client,
  reload,
}: {
  client: AmClientDetail;
  reload: () => void;
}) {
  const [form, setForm] = useState<Partial<AmStakeholder> | null>(null);
  const [expanded, setExpanded] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  const toggle = (id: string) =>
    setExpanded((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));

  async function remove(id: string) {
    try {
      await api.del(`/am/stakeholders/${id}`);
      reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not remove the stakeholder');
    }
  }

  return (
    <section className="card">
      <div className="card-head">
        <div className="grow">
          <span className="eyebrow">Who is on the account</span>
          <h2>Stakeholders</h2>
          <p className="sub">
            {client.stakeholders.filter((s) => s.IncludeInSbr).length} of{' '}
            {client.stakeholders.length} sit in the reviews.
          </p>
        </div>
        <button
          type="button"
          className="btn btn-primary btn-sm"
          onClick={() => setForm({ IncludeInSbr: true, IsPrimary: false })}
        >
          Add someone
        </button>
      </div>

      {error && (
        <div className="card-body">
          <ErrorBanner message={error} />
        </div>
      )}

      {client.stakeholders.length === 0 ? (
        <div className="card-body">
          <Empty title="Nobody recorded">
            <p>
              The primary contact is {client.PrimaryContact ?? 'not set'} — adding them here is what
              lets anything be recorded against them. Add the people involved in the account and
              the reviews.
            </p>
          </Empty>
        </div>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th style={{ width: 160 }}>Role</th>
                <th style={{ width: 200 }}>Email</th>
                <th style={{ width: 140 }}>Phone</th>
                <th style={{ width: 110 }}>Reviews</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {client.stakeholders.map((s) => {
                const open = expanded.includes(s.AmStakeholderId);
                return (
                  <Fragment key={s.AmStakeholderId}>
                    <tr>
                      <td>
                        <div className="strong">
                          {s.Name}
                          {s.IsPrimary && <span className="badge badge-good ml-2">Primary</span>}
                        </div>
                        {s.Birthday && (
                          <div className="xs muted">Birthday {formatBirthday(s.Birthday)}</div>
                        )}
                      </td>
                      <td className="xs">{s.Role ?? '—'}</td>
                      <td className="xs">{s.Email ?? '—'}</td>
                      <td className="xs">{s.Phone ?? '—'}</td>
                      <td>
                        {s.IncludeInSbr ? (
                          <span className="badge badge-good">Included</span>
                        ) : (
                          <span className="badge badge-neutral">Excluded</span>
                        )}
                      </td>
                      <td className="actions">
                        {hasRelationshipDetail(s) && (
                          <button
                            type="button"
                            className="btn btn-ghost btn-sm"
                            onClick={() => toggle(s.AmStakeholderId)}
                          >
                            {open ? '▲ Detail' : '▼ Detail'}
                          </button>
                        )}
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          onClick={() => setForm(s)}
                        >
                          Edit
                        </button>
                        <DeleteButton
                          label="Remove"
                          what={s.Name}
                          onConfirm={() => void remove(s.AmStakeholderId)}
                        />
                      </td>
                    </tr>
                    {open && (
                      <tr>
                        <td colSpan={6}>
                          <div className="facts">
                            {RELATIONSHIP_FIELDS.filter(([key]) =>
                              (s[key] ?? '').toString().trim(),
                            ).map(([key, label]) => (
                              <Fragment key={key}>
                                <dt>{label}</dt>
                                <dd>{key === 'Birthday' ? formatBirthday(s.Birthday) : s[key]}</dd>
                              </Fragment>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {form && (
        <StakeholderModal
          clientId={client.AmClientId}
          stakeholder={form}
          onClose={() => setForm(null)}
          onSaved={() => {
            setForm(null);
            reload();
          }}
        />
      )}
    </section>
  );
}

function StakeholderModal({
  clientId,
  stakeholder,
  onClose,
  onSaved,
}: {
  clientId: string;
  stakeholder: Partial<AmStakeholder>;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState(stakeholder);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = <K extends keyof AmStakeholder>(key: K, value: AmStakeholder[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  async function submit() {
    setSaving(true);
    setError(null);
    const body = {
      name: (form.Name ?? '').trim(),
      role: form.Role ?? '',
      email: form.Email ?? '',
      phone: form.Phone ?? '',
      isPrimary: form.IsPrimary ?? false,
      includeInSbr: form.IncludeInSbr ?? true,
      birthday: form.Birthday ? form.Birthday.slice(0, 10) : '',
      spouse: form.Spouse ?? '',
      kids: form.Kids ?? '',
      interests: form.Interests ?? '',
      lifeEvents: form.LifeEvents ?? '',
      notes: form.Notes ?? '',
    };
    try {
      if (form.AmStakeholderId) await api.put(`/am/stakeholders/${form.AmStakeholderId}`, body);
      else await api.post(`/am/clients/${clientId}/stakeholders`, body);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save');
      setSaving(false);
    }
  }

  return (
    <Modal
      title={form.AmStakeholderId ? 'Edit stakeholder' : 'Add stakeholder'}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={saving || !(form.Name ?? '').trim()}
            onClick={() => void submit()}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      {error && <ErrorBanner message={error} />}
      <div className="form-grid">
        <Field label="Name">
          <input className="input" value={form.Name ?? ''} onChange={(e) => set('Name', e.target.value)} />
        </Field>
        <Field label="Role or title">
          <input className="input" value={form.Role ?? ''} onChange={(e) => set('Role', e.target.value)} />
        </Field>
        {/* No email or phone on this card: whatever a stakeholder already has
            is kept as it is and saved back unchanged. */}
      </div>

      <div className="row wrap mt-4">
        <label className="checkbox">
          <input
            type="checkbox"
            checked={form.IsPrimary ?? false}
            onChange={(e) => set('IsPrimary', e.target.checked)}
          />
          Primary contact
        </label>
        {/* Says what ticking it does. It is not a label on this row — it
            replaces the name on the account card and clears the flag on
            whoever held it before, and somebody should know that before they
            tick it rather than after. */}
        <span className="xs muted">Ticking this puts their name on the account card.</span>
        <label className="checkbox">
          <input
            type="checkbox"
            checked={form.IncludeInSbr ?? true}
            onChange={(e) => set('IncludeInSbr', e.target.checked)}
          />
          Sits in the reviews
        </label>
      </div>

      <div className="divider mt-4" />
      <p className="eyebrow mt-4">Relationship</p>
      <p className="xs muted">
        Not trivia. This is what makes the next review a conversation rather than an invoice
        walkthrough.
      </p>

      <div className="form-grid mt-4">
        <Field label="Birthday" hint="Month and day only.">
          <BirthdayPicker value={form.Birthday ?? null} onChange={(v) => set('Birthday', v)} />
        </Field>
        <Field label="Spouse or partner">
          <input className="input" value={form.Spouse ?? ''} onChange={(e) => set('Spouse', e.target.value)} />
        </Field>
        <Field label="Kids">
          <input
            className="input"
            placeholder="e.g. Maya (8), Theo (5)"
            value={form.Kids ?? ''}
            onChange={(e) => set('Kids', e.target.value)}
          />
        </Field>
        <Field label="Interests">
          <input
            className="input"
            placeholder="e.g. golf, Formula 1, brisket"
            value={form.Interests ?? ''}
            onChange={(e) => set('Interests', e.target.value)}
          />
        </Field>
      </div>
      <Field label="Life events">
        <textarea
          className="textarea"
          rows={2}
          placeholder="Moves, promotions, weddings, a new baby, retirement plans…"
          value={form.LifeEvents ?? ''}
          onChange={(e) => set('LifeEvents', e.target.value)}
        />
      </Field>
      <Field label="Notes">
        <textarea
          className="textarea"
          rows={2}
          placeholder="Anything worth remembering before the next conversation"
          value={form.Notes ?? ''}
          onChange={(e) => set('Notes', e.target.value)}
        />
      </Field>
    </Modal>
  );
}

/* =========================================================================
   Reviews
   ========================================================================= */

/** One cadence on from the given review. */
function nextDateFor(sbr: AmSbr): string {
  const base = sbr.NextDate ? new Date(`${sbr.NextDate.slice(0, 10)}T00:00:00`) : new Date();
  if (sbr.Cadence === 'Weekly') base.setDate(base.getDate() + 7);
  else if (sbr.Cadence === 'Monthly') base.setMonth(base.getMonth() + 1);
  else if (sbr.Cadence === 'Bi-monthly') base.setMonth(base.getMonth() + 2);
  else if (sbr.Cadence === 'Bi-annual') base.setMonth(base.getMonth() + 6);
  else if (sbr.Cadence === 'Annual') base.setFullYear(base.getFullYear() + 1);
  else base.setMonth(base.getMonth() + 3);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${base.getFullYear()}-${pad(base.getMonth() + 1)}-${pad(base.getDate())}`;
}

interface SbrDraft {
  AmSbrId?: string;
  title: string;
  cadence: string;
  nextDate: string;
  stakeholderIds: string[];
  auditIds: string[];
  complianceIds: number[];
  recIds: string[];
  agenda: string;
  whatsNew: WhatsNew;
  discussion: BusinessDiscussion;
  securityAssessmentId: number | null;
  actionItems: ActionItem[];
  supportTakeaways: string[];
  /* The Technical Support slides this review adds itself (202), in the order
     they were added. Empty is the usual case. */
  supportSlides: SupportSlide[];
  showSupportRequests: boolean;
  showTicketSummary: boolean;
  /* Whether the User Feedback slide is shown — on unless turned off (153). */
  showUserFeedback: boolean;
  /* Whether the Services Provided slide (11) is shown — on unless turned off (153). */
  showServicesProvided: boolean;
  /* Whether the Active IT Projects slide (13) is shown — on unless turned off (153). */
  showActiveProjects: boolean;
  /* Whether the Asset Management slides (10) are shown — on unless turned off (154). */
  showAssetManagement: boolean;
  showAgentHealth: boolean;
  /* Screenshots for the Backup Review and Service Information slides, by
     slot. Only the slots touched on the form are here. */
  images: Partial<Record<SlideImageSlot, ImageDraft>>;
  serviceTalkingPoints: string[];
  backupTakeaways: string[];
  closingEmail: string;
  closingPhone: string;
  closingWebsite: string;
}

/** One slide screenshot on the form: its title, a newly picked file not yet
 *  uploaded, or a request to remove the saved one. */
interface ImageDraft {
  title: string;
  file: { contentType: string; base64: string; previewUrl: string } | null;
  remove: boolean;
}

const blankPoints = (): string[] => Array<string>(5).fill('');

function parsePoints(raw: string | null | undefined): string[] {
  const out = blankPoints();
  try {
    const parsed = raw ? (JSON.parse(raw) as unknown) : [];
    if (Array.isArray(parsed)) parsed.slice(0, 5).forEach((t, i) => typeof t === 'string' && (out[i] = t));
  } catch {
    /* none */
  }
  return out;
}

/** The titles of a review's saved screenshots, as the form's starting point. */
function imageDrafts(sbr: AmSbr | null): Partial<Record<SlideImageSlot, ImageDraft>> {
  const saved = parseSlideImages(sbr?.SlideImages);
  const out: Partial<Record<SlideImageSlot, ImageDraft>> = {};
  for (const slot of SLIDE_IMAGE_SLOTS) {
    if (saved[slot]) out[slot] = { title: saved[slot]!.title, file: null, remove: false };
  }
  return out;
}

/** The three entries on the review form, in slide order. */
const WHATS_NEW_FIELDS: { key: WhatsNewKey; label: string; name: string; detail: string }[] = [
  { key: 'product', label: '1. New product', name: 'Product name', detail: 'What it is' },
  { key: 'service', label: '2. Services', name: 'Service name', detail: 'What it is' },
  { key: 'people', label: '3. People', name: 'Employee name', detail: 'What they do for you' },
];

function ReviewsTab({
  client,
  compliance,
  reload,
  onPresent,
  onFollowup,
}: {
  client: AmClientDetail;
  /** The client's live assessments, so a review can say which it covers. */
  compliance: AmComplianceSummary[];
  reload: () => void;
  /** fullScreen: open in Presentation mode, filling the whole window. */
  onPresent: (sbrId: string, fullScreen?: boolean) => void;
  onFollowup: (sbrId: string) => void;
}) {
  const [form, setForm] = useState<SbrDraft | null>(null);
  const [error, setError] = useState<string | null>(null);
  /* The review whose notes are open. */
  const [notesFor, setNotesFor] = useState<AmSbr | null>(null);

  async function remove(id: string) {
    try {
      await api.del(`/am/sbrs/${id}`);
      reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not delete the review');
    }
  }

  /* Held: it stops showing as overdue. Reopen undoes it. */
  async function setComplete(id: string, complete: boolean) {
    setError(null);
    try {
      await api.put(`/am/sbrs/${id}/complete`, { complete });
      reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not update the review');
    }
  }

  /**
   * Book the next meeting in the series.
   *
   * The attendees and the agenda carry over; the audits do not, because they
   * have been reviewed. Only recommendations still at Proposed come along —
   * approved and declined ones are settled and re-presenting them wastes the
   * client's time.
   */
  function scheduleNext(sbr: AmSbr) {
    const previousRecIds = parseIdList(sbr.RecIds);
    const previousAuditIds = parseIdList(sbr.AuditIds);
    const carried = client.recommendations
      .filter(
        (r) =>
          r.Status === 'Proposed' &&
          (previousRecIds.includes(r.AmRecommendationId) ||
            (r.AmAuditId !== null && previousAuditIds.includes(r.AmAuditId))),
      )
      .map((r) => r.AmRecommendationId);

    setForm({
      title: sbr.Title ?? 'Business review',
      cadence: sbr.Cadence ?? 'Quarterly',
      nextDate: nextDateFor(sbr),
      stakeholderIds: parseIdList(sbr.StakeholderIds),
      auditIds: [],
      complianceIds: [],
      recIds: carried,
      agenda: sbr.Agenda ?? '',
      /* Not carried over: what was new last quarter is not news this one. */
      whatsNew: emptyWhatsNew(),
      /* Carried over: goals, challenges and open topics are usually still
         live at the next meeting, and are easier to edit than to retype. */
      discussion: parseDiscussion(sbr.BusinessDiscussion),
      /* Carried over: the same framework is usually tracked review to review. */
      securityAssessmentId: sbr.SecurityAssessmentId ?? null,
      /* Carried over: an action item not yet done is still one next time, and
         is easier to delete than to retype. */
      actionItems: parseActionItems(sbr.ActionItems),
      /* Not carried over: takeaways are about this period's tickets. */
      supportTakeaways: emptyTakeaways(),
      /* Not carried over either: an added slide is its own screenshots, and
         this review has none of them yet. */
      supportSlides: [],
      /* Carried over: whether the support slides are part of this client's
         reviews is a standing choice. */
      showSupportRequests: sbr.ShowSupportRequests ?? false,
      showTicketSummary: sbr.ShowTicketSummary ?? false,
      showUserFeedback: sbr.ShowUserFeedback ?? true,
      showServicesProvided: sbr.ShowServicesProvided ?? true,
      showActiveProjects: sbr.ShowActiveProjects ?? true,
      showAssetManagement: sbr.ShowAssetManagement ?? true,
      showAgentHealth: sbr.ShowAgentHealth ?? true,
      /* Not carried over: each review's screenshots are of its own period.
         The talking points are not either — they are about those screenshots. */
      images: {},
      serviceTalkingPoints: blankPoints(),
      backupTakeaways: emptyTakeaways(),
      /* Carried over: the same person presents review to review. */
      closingEmail: sbr.ClosingEmail ?? '',
      closingPhone: sbr.ClosingPhone ?? '',
      closingWebsite: sbr.ClosingWebsite ?? '',
    });
  }

  return (
    <section className="card">
      <div className="card-head">
        <div className="grow">
          <span className="eyebrow">Cadence</span>
          <h2>Business reviews</h2>
          <p className="sub">
            The reviews are the point: a client nobody has sat down with is a client somebody else is
            talking to.
          </p>
        </div>
        <button
          type="button"
          className="btn btn-primary btn-sm"
          onClick={() =>
            setForm({
              title: '',
              cadence: 'Quarterly',
              nextDate: '',
              stakeholderIds: client.stakeholders
                .filter((s) => s.IncludeInSbr)
                .map((s) => s.AmStakeholderId),
              auditIds: [],
              complianceIds: [],
              recIds: [],
              agenda: '',
              whatsNew: emptyWhatsNew(),
              discussion: emptyDiscussion(),
              securityAssessmentId: null,
              actionItems: emptyActionItems(),
              supportTakeaways: emptyTakeaways(),
              supportSlides: [],
              showSupportRequests: false,
              showTicketSummary: false,
              showUserFeedback: true,
              showServicesProvided: true,
              showActiveProjects: true,
              showAssetManagement: true,
              showAgentHealth: true,
              images: {},
              serviceTalkingPoints: blankPoints(),
              backupTakeaways: emptyTakeaways(),
              closingEmail: '',
              closingPhone: '',
              closingWebsite: '',
            })
          }
        >
          Schedule a review
        </button>
      </div>

      {error && (
        <div className="card-body">
          <ErrorBanner message={error} />
        </div>
      )}

      {client.sbrs.length === 0 ? (
        <div className="card-body">
          <Empty title="Nothing scheduled">
            <p>
              Set up a weekly, monthly, bi-monthly, quarterly, bi-annual or annual cadence
              for this account.
            </p>
          </Empty>
        </div>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Review</th>
                <th style={{ width: 100 }}>Cadence</th>
                <th style={{ width: 120 }}>Date</th>
                <th>Attendees</th>
                <th style={{ width: 130 }}>Covers</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {client.sbrs.map((sbr) => {
                const attendees = client.stakeholders.filter((s) =>
                  parseIdList(sbr.StakeholderIds).includes(s.AmStakeholderId),
                );
                const auditCount = parseIdList(sbr.AuditIds).length;
                const recCount = parseIdList(sbr.RecIds).length;
                return (
                  <tr key={sbr.AmSbrId}>
                    <td>
                      <div className="strong">{sbr.Title ?? 'Review'}</div>
                      {sbr.Agenda && <div className="xs muted">{sbr.Agenda}</div>}
                    </td>
                    <td className="xs">{sbr.Cadence ?? '—'}</td>
                    <td className="mono xs">
                      {formatDate(sbr.NextDate)}
                      {sbr.CompletedUtc ? (
                        <div>
                          <span
                            className="badge badge-good"
                            title={`Completed ${formatDate(sbr.CompletedUtc)}${sbr.CompletedBy ? ` by ${sbr.CompletedBy}` : ''}`}
                          >
                            Completed
                          </span>
                        </div>
                      ) : (
                        sbr.NextDate &&
                        sbr.NextDate.slice(0, 10) < todayLocal() && (
                          <div>
                            <span className="badge badge-critical">Past due</span>
                          </div>
                        )
                      )}
                    </td>
                    <td className="xs muted">
                      {attendees.length > 0 ? attendees.map((a) => a.Name).join(', ') : '—'}
                    </td>
                    <td className="xs muted">
                      {auditCount} audit{auditCount === 1 ? '' : 's'} · {recCount} rec
                      {recCount === 1 ? '' : 's'}
                    </td>
                    <td className="actions">
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={() => onPresent(sbr.AmSbrId)}
                      >
                        Present
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => onPresent(sbr.AmSbrId, true)}
                        title="The deck fills the whole window — Esc to leave"
                      >
                        Presentation mode
                      </button>
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={() => onFollowup(sbr.AmSbrId)}
                      >
                        Follow-up
                      </button>
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={() => setNotesFor(sbr)}
                        title={sbr.Notes ? 'Open the notes kept on this review' : 'Add notes to this review'}
                      >
                        {sbr.Notes?.trim() ? `Notes (${sbr.Notes.length > 999 ? `${(sbr.Notes.length / 1000).toFixed(1)}k` : sbr.Notes.length})` : 'Add notes'}
                      </button>
                      {sbr.CompletedUtc ? (
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          title="Not held after all — it counts as due (or overdue) again"
                          onClick={() => void setComplete(sbr.AmSbrId, false)}
                        >
                          Reopen
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          title="The review was held — it stops showing as overdue"
                          onClick={() => void setComplete(sbr.AmSbrId, true)}
                        >
                          ✓ Mark complete
                        </button>
                      )}
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        title="Book the next meeting in this series"
                        onClick={() => scheduleNext(sbr)}
                      >
                        Next →
                      </button>
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={() =>
                          setForm({
                            AmSbrId: sbr.AmSbrId,
                            title: sbr.Title ?? '',
                            cadence: sbr.Cadence ?? 'Quarterly',
                            nextDate: sbr.NextDate?.slice(0, 10) ?? '',
                            stakeholderIds: parseIdList(sbr.StakeholderIds),
                            auditIds: parseIdList(sbr.AuditIds),
                            complianceIds: parseNumberIdList(sbr.ComplianceIds),
                            recIds: parseIdList(sbr.RecIds),
                            agenda: sbr.Agenda ?? '',
                            whatsNew: parseWhatsNew(sbr.WhatsNew),
                            discussion: parseDiscussion(sbr.BusinessDiscussion),
                            securityAssessmentId: sbr.SecurityAssessmentId ?? null,
                            actionItems: parseActionItems(sbr.ActionItems),
                            supportTakeaways: parseTakeaways(sbr.SupportTakeaways),
                            supportSlides: parseSupportSlides(sbr.SupportSlides),
                            showSupportRequests: sbr.ShowSupportRequests ?? false,
                            showTicketSummary: sbr.ShowTicketSummary ?? false,
                            showUserFeedback: sbr.ShowUserFeedback ?? true,
                            showServicesProvided: sbr.ShowServicesProvided ?? true,
                            showActiveProjects: sbr.ShowActiveProjects ?? true,
                            showAssetManagement: sbr.ShowAssetManagement ?? true,
                            showAgentHealth: sbr.ShowAgentHealth ?? true,
                            images: imageDrafts(sbr),
                            serviceTalkingPoints: parsePoints(sbr.ServiceTalkingPoints),
                            backupTakeaways: parseTakeaways(sbr.BackupTakeaways),
                            closingEmail: sbr.ClosingEmail ?? '',
                            closingPhone: sbr.ClosingPhone ?? '',
                            closingWebsite: sbr.ClosingWebsite ?? '',
                          })
                        }
                      >
                        Edit
                      </button>
                      <DeleteButton
                        what={sbr.Title ?? 'this review'}
                        onConfirm={() => void remove(sbr.AmSbrId)}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {notesFor && (
        <ReviewNotesModal sbr={notesFor} clientName={client.Name} onClose={() => setNotesFor(null)} onSaved={reload} />
      )}

      {form && (
        <ReviewModal
          client={client}
          compliance={compliance}
          draft={form}
          onClose={() => setForm(null)}
          onSaved={() => {
            setForm(null);
            reload();
          }}
        />
      )}
    </section>
  );
}


function ReviewModal({
  client,
  compliance,
  draft,
  onClose,
  onSaved,
}: {
  client: AmClientDetail;
  compliance: AmComplianceSummary[];
  draft: SbrDraft;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState(draft);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /* The thank-you slide's email falls back to the account manager's. */
  const manager = useResource<{ Email: string | null }>(
    client.AccountManagerEmployeeId ? `/employees/${client.AccountManagerEmployeeId}` : null,
  );
  const managerEmail = manager.data?.Email ?? null;

  /* Audits already covered by another review are hidden — except the ones on
     the review being edited, so its own selection stays visible. */
  const coveredElsewhere = new Set(
    client.sbrs
      .filter((s) => s.AmSbrId !== form.AmSbrId)
      .flatMap((s) => parseIdList(s.AuditIds)),
  );
  const availableAudits = client.audits.filter(
    (a) => !coveredElsewhere.has(a.AmAuditId) || form.auditIds.includes(a.AmAuditId),
  );

  /* Recommendations from the selected audits, plus anything carried over from a
     previous meeting (still listed so it can be unticked). */
  const candidateRecs = client.recommendations.filter(
    (r) =>
      (r.AmAuditId !== null && form.auditIds.includes(r.AmAuditId)) ||
      form.recIds.includes(r.AmRecommendationId),
  );

  const toggleIn = (list: string[], id: string) =>
    list.includes(id) ? list.filter((x) => x !== id) : [...list, id];

  /** Selecting an audit brings its recommendations with it; deselecting removes
   *  them again. Otherwise every attach is two steps and one of them is
   *  forgettable. */
  function toggleAudit(auditId: string) {
    const auditRecIds = client.recommendations
      .filter((r) => r.AmAuditId === auditId)
      .map((r) => r.AmRecommendationId);
    setForm((f) =>
      f.auditIds.includes(auditId)
        ? {
            ...f,
            auditIds: f.auditIds.filter((x) => x !== auditId),
            recIds: f.recIds.filter((id) => !auditRecIds.includes(id)),
          }
        : {
            ...f,
            auditIds: [...f.auditIds, auditId],
            recIds: [...new Set([...f.recIds, ...auditRecIds])],
          },
    );
  }

  async function submit() {
    setSaving(true);
    setError(null);
    const body = {
      title: form.title.trim(),
      cadence: form.cadence,
      nextDate: form.nextDate,
      stakeholderIds: form.stakeholderIds,
      auditIds: form.auditIds,
      recIds: form.recIds,
      complianceIds: form.complianceIds,
      agenda: form.agenda,
      whatsNew: form.whatsNew,
      businessDiscussion: form.discussion,
      securityAssessmentId: form.securityAssessmentId,
      actionItems: form.actionItems,
      supportTakeaways: form.supportTakeaways,
      supportSlides: form.supportSlides,
      showSupportRequests: form.showSupportRequests,
      showTicketSummary: form.showTicketSummary,
      showUserFeedback: form.showUserFeedback,
      showServicesProvided: form.showServicesProvided,
      showActiveProjects: form.showActiveProjects,
      showAssetManagement: form.showAssetManagement,
      showAgentHealth: form.showAgentHealth,
      serviceTalkingPoints: form.serviceTalkingPoints,
      backupTakeaways: form.backupTakeaways,
      closingEmail: form.closingEmail,
      closingPhone: form.closingPhone,
      closingWebsite: form.closingWebsite,
    };
    try {
      let id = form.AmSbrId;
      if (id) await api.put(`/am/sbrs/${id}`, body);
      else id = (await api.post<{ AmSbrId: string }>(`/am/clients/${client.AmClientId}/sbrs`, body))?.AmSbrId;

      /* Screenshots go after the review, because a new review has no id until
         it is saved. One request per slot that changed. */
      if (id) {
        for (const slot of SLIDE_IMAGE_SLOTS) {
          const d = form.images[slot];
          if (!d) continue;
          const path = `/am/sbrs/${id}/images/${slot}`;
          const had = Boolean(savedImages[slot]);
          if (d.file) {
            await api.put(path, { title: d.title, contentType: d.file.contentType, content: d.file.base64 });
          } else if (d.remove && had) {
            await api.del(path);
          } else if (had && d.title !== (savedImages[slot]?.title ?? '')) {
            await api.put(path, { title: d.title });
          }
        }
      }
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the review');
      setSaving(false);
    }
  }

  /* The screenshots already saved on the review being edited. */
  const editingSbr = client.sbrs.find((s) => s.AmSbrId === form.AmSbrId) ?? null;
  const savedImages = parseSlideImages(editingSbr?.SlideImages);
  const setImage = (slot: SlideImageSlot, patch: Partial<ImageDraft>) =>
    setForm((d) => ({
      ...d,
      images: {
        ...d.images,
        [slot]: { title: '', file: null, remove: false, ...d.images[slot], ...patch },
      },
    }));

  /* The review's own Technical Support slides (202). Each owns a pair of
     screenshot slots for as long as it is on the form: adding one takes the
     first free pair and forgets whatever was drafted against it, removing
     one gives the pair back and deletes the screenshots on it when the
     review is saved. */
  const setSupportSlide = (index: number, patch: Partial<SupportSlide>) =>
    setForm((d) => ({
      ...d,
      supportSlides: d.supportSlides.map((s, i) => (i === index ? { ...s, ...patch } : s)),
    }));

  const addSupportSlide = () =>
    setForm((d) => {
      const pair = freeSupportPair(d.supportSlides);
      if (pair === null) return d;
      const images = { ...d.images };
      for (const slot of SUPPORT_SLIDE_SLOTS[pair] ?? []) delete images[slot];
      return { ...d, images, supportSlides: [...d.supportSlides, emptySupportSlide(pair)] };
    });

  const removeSupportSlide = (index: number) =>
    setForm((d) => {
      const slide = d.supportSlides[index];
      if (!slide) return d;
      const images = { ...d.images };
      for (const slot of SUPPORT_SLIDE_SLOTS[slide.pair] ?? []) {
        images[slot] = { title: '', file: null, remove: true };
      }
      return { ...d, images, supportSlides: d.supportSlides.filter((_, i) => i !== index) };
    });

  return (
    <Modal
      title={form.AmSbrId ? 'Edit review' : 'Schedule a review'}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={saving}
            onClick={() => void submit()}
          >
            {saving ? 'Saving…' : form.AmSbrId ? 'Save' : 'Schedule'}
          </button>
        </>
      }
    >
      {error && <ErrorBanner message={error} />}
      <SlideHeading slide="Review details" title="Who, when, and which audits and recommendations" />
      <Field label="Title">
        <input
          className="input"
          placeholder={`${form.cadence} business review`}
          value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
        />
      </Field>
      <div className="form-grid">
        <Field label="Cadence">
          <select
            className="select"
            value={form.cadence}
            onChange={(e) => setForm({ ...form, cadence: e.target.value })}
          >
            {CADENCES.map((c) => (
              <option key={c}>{c}</option>
            ))}
          </select>
        </Field>
        <Field label="Meeting date">
          <input
            className="input"
            type="date"
            value={form.nextDate}
            onChange={(e) => setForm({ ...form, nextDate: e.target.value })}
          />
        </Field>
      </div>

      <Field label={`Attendees (${form.stakeholderIds.length})`}>
        {client.stakeholders.length === 0 ? (
          <p className="xs muted">Add stakeholders first, then assign attendees.</p>
        ) : (
          <div className="stack" style={{ gap: 'var(--space-1)' }}>
            {client.stakeholders.map((s) => (
              <label key={s.AmStakeholderId} className="checkbox">
                <input
                  type="checkbox"
                  checked={form.stakeholderIds.includes(s.AmStakeholderId)}
                  onChange={() =>
                    setForm({
                      ...form,
                      stakeholderIds: toggleIn(form.stakeholderIds, s.AmStakeholderId),
                    })
                  }
                />
                {s.Name} <span className="xs muted">{s.Role}</span>
              </label>
            ))}
          </div>
        )}
      </Field>

      <Field label="Audits reviewed in this meeting">
        {availableAudits.length === 0 ? (
          <p className="xs muted">
            {client.audits.length === 0
              ? 'No audits for this client yet — run one from the audits tab.'
              : 'Every audit is already covered by another review.'}
          </p>
        ) : (
          <div className="stack" style={{ gap: 'var(--space-1)' }}>
            {availableAudits.map((a) => (
              <label key={a.AmAuditId} className="checkbox">
                <input
                  type="checkbox"
                  checked={form.auditIds.includes(a.AmAuditId)}
                  onChange={() => toggleAudit(a.AmAuditId)}
                />
                {a.Name} <span className="xs muted">{a.SetName}</span>
                <StateBadge status={a.Status} />
              </label>
            ))}
          </div>
        )}
      </Field>

      {(form.auditIds.length > 0 || candidateRecs.length > 0) && (
        <Field label={`Recommendations to present (${form.recIds.length} selected)`}>
          {candidateRecs.length === 0 ? (
            <p className="xs muted">The selected audits have no recommendations yet.</p>
          ) : (
            <div className="stack" style={{ gap: 'var(--space-1)' }}>
              {candidateRecs.map((r) => (
                <label key={r.AmRecommendationId} className="checkbox">
                  <input
                    type="checkbox"
                    checked={form.recIds.includes(r.AmRecommendationId)}
                    onChange={() =>
                      setForm({ ...form, recIds: toggleIn(form.recIds, r.AmRecommendationId) })
                    }
                  />
                  <span className="grow">{r.Title}</span>
                  <PriorityBadge priority={topPriority(r)} />
                  <StateBadge status={r.Status} />
                  {(r.AmAuditId === null || !form.auditIds.includes(r.AmAuditId)) && (
                    <span className="badge badge-neutral">Carried over</span>
                  )}
                </label>
              ))}
            </div>
          )}
        </Field>
      )}

      <Field label="Agenda">
        <textarea
          className="textarea"
          rows={3}
          value={form.agenda}
          onChange={(e) => setForm({ ...form, agenda: e.target.value })}
        />
      </Field>

      <SlideHeading slide="Slide 3" title="What’s new at CMIT Solutions" thumbs={[thumbWhatsNew]} />
      <Field
        label="What’s new at CMIT Solutions"
        hint="The third slide of the presentation. Add as many lines under a heading as you have news; fill in any of them and the slide shows what you filled in, leave them all empty and it is left out."
      >
        <div className="stack" style={{ gap: 'var(--space-3)' }}>
          {WHATS_NEW_FIELDS.map((f) => (
            <div key={f.key}>
              <div className="xs strong" style={{ marginBottom: 4 }}>{f.label}</div>
              <div className="stack" style={{ gap: 'var(--space-2)' }}>
                {form.whatsNew[f.key].map((item, i) => (
                  <div key={i} className="am-line-row">
                    <input
                      className="input"
                      placeholder={f.name}
                      value={item.name}
                      onChange={(e) =>
                        setForm((d) => ({
                          ...d,
                          whatsNew: {
                            ...d.whatsNew,
                            [f.key]: d.whatsNew[f.key].map((x, j) =>
                              j === i ? { ...x, name: e.target.value } : x,
                            ),
                          },
                        }))
                      }
                    />
                    <input
                      className="input"
                      placeholder={f.detail}
                      value={item.detail}
                      onChange={(e) =>
                        setForm((d) => ({
                          ...d,
                          whatsNew: {
                            ...d.whatsNew,
                            [f.key]: d.whatsNew[f.key].map((x, j) =>
                              j === i ? { ...x, detail: e.target.value } : x,
                            ),
                          },
                        }))
                      }
                    />
                    {form.whatsNew[f.key].length > 1 && (
                      <button
                        type="button"
                        className="btn btn-ghost btn-icon btn-sm"
                        title="Remove this line"
                        onClick={() =>
                          setForm((d) => ({
                            ...d,
                            whatsNew: {
                              ...d.whatsNew,
                              [f.key]: d.whatsNew[f.key].filter((_, j) => j !== i),
                            },
                          }))
                        }
                      >
                        ✕
                      </button>
                    )}
                  </div>
                ))}
              </div>
              {form.whatsNew[f.key].length < MAX_WHATS_NEW_LINES && (
                <button
                  type="button"
                  className="btn btn-ghost btn-sm mt-2"
                  onClick={() =>
                    setForm((d) => ({
                      ...d,
                      whatsNew: {
                        ...d.whatsNew,
                        [f.key]: [...d.whatsNew[f.key], { name: '', detail: '' }],
                      },
                    }))
                  }
                >
                  + Add a line
                </button>
              )}
            </div>
          ))}
        </div>
      </Field>

      <SlideHeading slide="Slide 4" title="Business Discussion" thumbs={[thumbDiscussion]} />
      <Field
        label="Business discussion"
        hint="Its own slide after What’s new. Three lines under each heading to start, and add more where you need them; fill in any of them and the slide shows what you filled in, leave them all empty and it is left out."
      >
        <div className="am-discuss-form">
          {DISCUSSION_COLUMNS.flatMap((col) => col.lists).map((list) => (
            <div key={list.key}>
              <div className="xs strong" style={{ marginBottom: 4 }}>
                {list.label}
              </div>
              <div className="stack" style={{ gap: 'var(--space-1)' }}>
                {form.discussion[list.key].map((value, i) => (
                  <div key={i} className="am-line-row">
                    <input
                      className="input"
                      placeholder={`${i + 1}.`}
                      value={value}
                      onChange={(e) =>
                        setForm((d) => ({
                          ...d,
                          discussion: {
                            ...d.discussion,
                            [list.key]: d.discussion[list.key].map((v, j) =>
                              j === i ? e.target.value : v,
                            ),
                          },
                        }))
                      }
                    />
                    {form.discussion[list.key].length > 1 && (
                      <button
                        type="button"
                        className="btn btn-ghost btn-icon btn-sm"
                        title="Remove this line"
                        onClick={() =>
                          setForm((d) => ({
                            ...d,
                            discussion: {
                              ...d.discussion,
                              [list.key]: d.discussion[list.key].filter((_, j) => j !== i),
                            },
                          }))
                        }
                      >
                        ✕
                      </button>
                    )}
                  </div>
                ))}
              </div>
              {form.discussion[list.key].length < MAX_DISCUSSION_ITEMS && (
                <button
                  type="button"
                  className="btn btn-ghost btn-sm mt-2"
                  onClick={() =>
                    setForm((d) => ({
                      ...d,
                      discussion: { ...d.discussion, [list.key]: [...d.discussion[list.key], ''] },
                    }))
                  }
                >
                  + Add a line
                </button>
              )}
            </div>
          ))}
        </div>
      </Field>

      <SlideHeading slide="After the audit slides" title="Action Items/Recommendations" thumbs={[thumbActions]} />
      <Field
        label="Action items"
        hint="Up to six of your own, shown on the Action Items/Recommendations slide after the recommendations from this review’s audits. Leave a row empty to skip it."
      >
        <div className="stack" style={{ gap: 'var(--space-2)' }}>
          {form.actionItems.map((item, i) => (
            <div key={i} className="form-grid">
              <input
                className="input"
                placeholder={`${i + 1}. Title`}
                value={item.title}
                onChange={(e) =>
                  setForm((d) => ({
                    ...d,
                    actionItems: d.actionItems.map((x, j) => (j === i ? { ...x, title: e.target.value } : x)),
                  }))
                }
              />
              <input
                className="input"
                placeholder="What it is"
                value={item.detail}
                onChange={(e) =>
                  setForm((d) => ({
                    ...d,
                    actionItems: d.actionItems.map((x, j) => (j === i ? { ...x, detail: e.target.value } : x)),
                  }))
                }
              />
            </div>
          ))}
        </div>
      </Field>

      <SlideHeading slide="Slides 5 & 6" title="Technical Support — Support Request Summary and Ticket Summary" thumbs={[thumbSupport, thumbTickets]} />
      <Field
        label="Technical support"
        hint="Two slides after Business discussion. Tick the ones to show in this review."
      >
        <div className="stack" style={{ gap: 'var(--space-1)' }}>
          <label className="checkbox">
            <input
              type="checkbox"
              checked={form.showSupportRequests}
              onChange={(e) => setForm((d) => ({ ...d, showSupportRequests: e.target.checked }))}
            />
            Show Support Request Summary — tickets created vs. completed over six months, live from Autotask, with takeaways
          </label>
          <label className="checkbox">
            <input
              type="checkbox"
              checked={form.showTicketSummary}
              onChange={(e) => setForm((d) => ({ ...d, showTicketSummary: e.target.checked }))}
            />
            Show Ticket Summary — tickets created in the past 3 months by employee and by issue type, live from Autotask
          </label>
        </div>
      </Field>

      {/* Both Technical Support slides read from it, so it shows when either
          is ticked. */}
      {(form.showSupportRequests || form.showTicketSummary) && (
        <Field
          label="Autotask company"
          hint="The ticket slides use this client's own company in Autotask, found by its name — nothing to pick."
        >
          <AutotaskCompanyLink clientId={client.AmClientId} />
        </Field>
      )}

      {form.showSupportRequests && (
      <Field
        label="Technical support takeaways"
        hint="Up to five, shown beside the ticket chart on the Support Request Summary slide. Leave a line empty to skip it."
      >
        <div className="stack" style={{ gap: 'var(--space-1)' }}>
          {form.supportTakeaways.map((value, i) => (
            <input
              key={i}
              className="input"
              placeholder={`${i + 1}.`}
              value={value}
              onChange={(e) =>
                setForm((d) => ({
                  ...d,
                  supportTakeaways: d.supportTakeaways.map((t, j) => (j === i ? e.target.value : t)),
                }))
              }
            />
          ))}
        </div>
      </Field>
      )}

      {/* The review's own Technical Support slides (202): blank ones it adds
          itself, for anything the two live slides above do not cover. */}
      <Field
        label="Add a Technical Support slide"
        hint="Blank slides under the same heading, up to four, shown after the two above. Each takes two screenshots with a description under each and up to five takeaways beside them."
      >
        <div className="stack" style={{ gap: 'var(--space-4)' }}>
          {form.supportSlides.map((slide, i) => (
            <div key={slide.pair} className="rv-added-slide">
              <div className="row">
                <input
                  className="input grow"
                  placeholder={`Slide heading, e.g. Patching and maintenance`}
                  value={slide.heading}
                  onChange={(e) => setSupportSlide(i, { heading: e.target.value })}
                />
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => removeSupportSlide(i)}>
                  Remove slide
                </button>
              </div>
              <div className="am-service-form mt-3">
                {(SUPPORT_SLIDE_SLOTS[slide.pair] ?? []).map((slot, j) => (
                  <div key={slot} className="stack" style={{ gap: 'var(--space-2)' }}>
                    <SlideImageField
                      sbrId={editingSbr?.AmSbrId ?? null}
                      slot={slot}
                      saved={savedImages[slot]}
                      draft={form.images[slot]}
                      titlePlaceholder={`Screenshot ${j + 1} caption`}
                      onChange={(patch) => setImage(slot, patch)}
                      onError={setError}
                    />
                    <textarea
                      className="textarea"
                      rows={3}
                      placeholder={`What screenshot ${j + 1} shows — the description under it on the slide`}
                      value={slide.notes[j] ?? ''}
                      onChange={(e) =>
                        setSupportSlide(i, {
                          notes: slide.notes.map((n, k) => (k === j ? e.target.value : n)),
                        })
                      }
                    />
                  </div>
                ))}
              </div>
              <div className="stack mt-3" style={{ gap: 'var(--space-1)' }}>
                <div className="xs strong">Takeaways</div>
                {slide.takeaways.map((value, k) => (
                  <input
                    key={k}
                    className="input"
                    placeholder={`${k + 1}.`}
                    value={value}
                    onChange={(e) =>
                      setSupportSlide(i, {
                        takeaways: slide.takeaways.map((t, x) => (x === k ? e.target.value : t)),
                      })
                    }
                  />
                ))}
              </div>
            </div>
          ))}
          <div className="row">
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              disabled={form.supportSlides.length >= MAX_SUPPORT_SLIDES}
              onClick={addSupportSlide}
            >
              Add a Technical Support slide
            </button>
            {form.supportSlides.length >= MAX_SUPPORT_SLIDES && (
              <span className="xs muted">Four is as many as one review can add.</span>
            )}
          </div>
        </div>
      </Field>

      <SlideHeading slide="Slide 7" title="Backup Review" thumbs={[thumbBackup]} />
      <Field
        label="Backup review"
        hint="Its own slide after Technical support: up to three screenshots from the backup console, each with a caption, and up to five takeaways beside them. Nothing filled in, no slide."
      >
        <div className="am-service-form">
          {BACKUP_SLOTS.map((slot, i) => (
            <SlideImageField
              key={slot}
              sbrId={editingSbr?.AmSbrId ?? null}
              slot={slot}
              saved={savedImages[slot]}
              draft={form.images[slot]}
              titlePlaceholder={i === 0 ? 'Caption, e.g. Datto backups — last 90 days' : `Screenshot ${i + 1} caption`}
              onChange={(patch) => setImage(slot, patch)}
              onError={setError}
            />
          ))}
        </div>
        <div className="mt-2">
          <div className="xs strong" style={{ marginBottom: 4 }}>
            Takeaways
          </div>
          <div className="stack" style={{ gap: 'var(--space-1)' }}>
            {form.backupTakeaways.map((value, i) => (
              <input
                key={i}
                className="input"
                placeholder={`${i + 1}.`}
                value={value}
                onChange={(e) =>
                  setForm((d) => ({
                    ...d,
                    backupTakeaways: d.backupTakeaways.map((t, j) => (j === i ? e.target.value : t)),
                  }))
                }
              />
            ))}
          </div>
        </div>
      </Field>

      <SlideHeading slide="Slide 8" title="Service Information" thumbs={[thumbService]} />
      <Field
        label="Service information"
        hint="Its own slide after Backup review: up to three screenshots from the security tools, each with a caption, and up to five takeaways beside them. Nothing filled in, no slide."
      >
        <div className="am-service-form">
          {SERVICE_SLOTS.map((slot, i) => (
            <SlideImageField
              key={slot}
              sbrId={editingSbr?.AmSbrId ?? null}
              slot={slot}
              saved={savedImages[slot]}
              draft={form.images[slot]}
              titlePlaceholder={`Screenshot ${i + 1} caption`}
              onChange={(patch) => setImage(slot, patch)}
              onError={setError}
            />
          ))}
        </div>
        <div className="stack mt-2" style={{ gap: 'var(--space-1)' }}>
          <div className="xs strong">Takeaways</div>
          {form.serviceTalkingPoints.map((value, i) => (
            <input
              key={i}
              className="input"
              placeholder={`${i + 1}.`}
              value={value}
              onChange={(e) =>
                setForm((d) => ({
                  ...d,
                  serviceTalkingPoints: d.serviceTalkingPoints.map((t, j) => (j === i ? e.target.value : t)),
                }))
              }
            />
          ))}
        </div>
      </Field>

      <SlideHeading slide="Slide 9" title="User Feedback" thumbs={[thumbFeedback]} />
      <label className="checkbox mb-2">
        <input
          type="checkbox"
          checked={form.showUserFeedback}
          onChange={(e) => setForm((d) => ({ ...d, showUserFeedback: e.target.checked }))}
        />
        Show the User Feedback slide in this review
      </label>
      {form.showUserFeedback && <CsatPreview clientId={client.AmClientId} />}
      <details className="am-csat-fallback mt-2">
        <summary className="xs">Screenshots instead — used only when the client is not linked to Autotask</summary>
      <Field
        label="User feedback"
        hint="Filled from Autotask: the client's ticket survey results for the last twelve months, and the average score dial. These screenshots are used only when the client is not linked to an Autotask company."
      >
        <div className="am-service-form">
          {FEEDBACK_SLOTS.map((slot, i) => (
            <SlideImageField
              key={slot}
              sbrId={editingSbr?.AmSbrId ?? null}
              slot={slot}
              saved={savedImages[slot]}
              draft={form.images[slot]}
              titlePlaceholder={i === 0 ? 'Large screenshot caption, e.g. Survey results — last 90 days' : 'Small screenshot caption (upper right)'}
              onChange={(patch) => setImage(slot, patch)}
              onError={setError}
            />
          ))}
        </div>
      </Field>
      </details>

      <SlideHeading slide="Slide 10" title="Asset Management" />
      <label className="checkbox mb-2">
        <input
          type="checkbox"
          checked={form.showAssetManagement}
          onChange={(e) => setForm((d) => ({ ...d, showAssetManagement: e.target.checked }))}
        />
        Show the Asset Management slides in this review
      </label>
      {form.showAssetManagement && <AssetsPreview clientId={client.AmClientId} />}

      <SlideHeading slide="Slide 10a" title="Agent Health" />
      <label className="checkbox mb-2">
        <input
          type="checkbox"
          checked={form.showAgentHealth}
          onChange={(e) => setForm((d) => ({ ...d, showAgentHealth: e.target.checked }))}
        />
        Show the Agent Health slide in this review
      </label>
      {form.showAgentHealth && <AgentHealthPreview clientId={client.AmClientId} />}
      {form.showAgentHealth && (
        <Field
          label="Agent health screenshot"
          hint="Optional. When one is uploaded, the slide shows it instead of the live Automate figures — for a client not linked to Automate, or a view from the RMM you would rather show."
        >
          <SlideImageField
            sbrId={editingSbr?.AmSbrId ?? null}
            slot="agentHealth"
            saved={savedImages.agentHealth}
            draft={form.images.agentHealth}
            titlePlaceholder="Caption, e.g. Agent status — October"
            onChange={(patch) => setImage('agentHealth', patch)}
            onError={setError}
          />
        </Field>
      )}

      <SlideHeading slide="Slide 11" title="Services Provided" />
      <label className="checkbox mb-2">
        <input
          type="checkbox"
          checked={form.showServicesProvided}
          onChange={(e) => setForm((d) => ({ ...d, showServicesProvided: e.target.checked }))}
        />
        Show the Services Provided slide in this review
      </label>
      {form.showServicesProvided && <ServicesPreview clientId={client.AmClientId} />}

      <SlideHeading slide="Slide 12" title="Security Maturity" thumbs={[thumbSecurity]} />
      <Field
        label="Security maturity"
        hint="Its own slide after Business discussion: the rose, where they stand and what is outstanding for the assessment you pick. Choose none and the slide is left out."
      >
        {compliance.length === 0 ? (
          <p className="xs muted">
            This client has no compliance assessment started yet — start one in the Compliance
            module and it can be picked here.
          </p>
        ) : (
          <select
            className="select"
            value={form.securityAssessmentId ?? ''}
            onChange={(e) =>
              setForm((d) => ({
                ...d,
                securityAssessmentId: e.target.value === '' ? null : Number(e.target.value),
              }))
            }
          >
            <option value="">— No security maturity slide —</option>
            {compliance.map((a) => (
              <option key={a.AssessmentId} value={a.AssessmentId}>
                {a.FrameworkName}
                {a.ScopeLevelLabel ? ` · ${a.ScopeLevelLabel}` : ''}
                {a.ScorePct === null ? '' : ` · ${a.ScorePct}% met`}
                {a.Status === 'complete' ? ' · complete' : ' · in progress'}
              </option>
            ))}
          </select>
        )}
      </Field>

      <SlideHeading slide="Slide 13" title="Active IT Projects" />
      <label className="checkbox mb-2">
        <input
          type="checkbox"
          checked={form.showActiveProjects}
          onChange={(e) => setForm((d) => ({ ...d, showActiveProjects: e.target.checked }))}
        />
        Show the Active IT Projects slide in this review
      </label>
      {form.showActiveProjects && <ProjectsPreview clientId={client.AmClientId} />}

      <SlideHeading slide="Last two slides" title="Questions? and Thank you — only the contact lines to fill in" thumbs={[thumbQuestions, thumbThankYou]} />
      <Field
        label="Thank-you slide contact"
        hint="The last slide's contact information. Leave a line empty to use the default shown; an empty phone is left off the slide."
      >
        <div className="form-grid">
          <input
            className="input"
            type="email"
            placeholder={managerEmail ?? 'Email'}
            value={form.closingEmail}
            onChange={(e) => setForm((d) => ({ ...d, closingEmail: e.target.value }))}
          />
          <input
            className="input"
            placeholder="Phone, e.g. 303.555.0100"
            value={form.closingPhone}
            onChange={(e) => setForm((d) => ({ ...d, closingPhone: e.target.value }))}
          />
          <input
            className="input"
            placeholder={DEFAULT_CLOSING_WEBSITE}
            value={form.closingWebsite}
            onChange={(e) => setForm((d) => ({ ...d, closingWebsite: e.target.value }))}
          />
        </div>
      </Field>
    </Modal>
  );
}

/**
 * One slide screenshot on the review form: a caption, the picture (the saved
 * one until replaced or removed), and upload / replace / remove. Nothing is
 * sent until the review is saved.
 */
function SlideImageField({
  sbrId,
  slot,
  saved,
  draft,
  titlePlaceholder,
  onChange,
  onError,
}: {
  sbrId: string | null;
  slot: SlideImageSlot;
  saved: SavedSlideImage | undefined;
  draft: ImageDraft | undefined;
  titlePlaceholder: string;
  onChange: (patch: Partial<ImageDraft>) => void;
  onError: (message: string) => void;
}) {
  const savedUrl = useAuthedImage(sbrId && saved ? imagePath(sbrId, slot, saved.uploadedUtc) : null);
  const preview = draft?.file?.previewUrl ?? (draft?.remove ? null : savedUrl);
  const title = draft?.title ?? saved?.title ?? '';

  function pick(file: File | undefined) {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      onError('A screenshot must be an image (PNG or JPEG).');
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      onError('That image is larger than 8 MB.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result);
      onChange({
        title,
        file: { contentType: file.type, base64: dataUrl.split(',')[1] ?? '', previewUrl: dataUrl },
        remove: false,
      });
    };
    reader.readAsDataURL(file);
  }

  return (
    <div className="stack" style={{ gap: 'var(--space-2)' }}>
      <input
        className="input"
        placeholder={titlePlaceholder}
        value={title}
        onChange={(e) => onChange({ title: e.target.value })}
      />
      {preview && <img className="am-backup-thumb" src={preview} alt={title || 'Screenshot'} />}
      <div className="row wrap">
        <label className="btn btn-ghost btn-sm">
          {preview ? 'Replace screenshot' : 'Upload screenshot'}
          <input
            type="file"
            accept="image/png,image/jpeg,image/gif,image/webp"
            style={{ display: 'none' }}
            onChange={(e) => {
              pick(e.target.files?.[0]);
              e.target.value = '';
            }}
          />
        </label>
        {preview && (
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => onChange({ title, file: null, remove: true })}
          >
            Remove
          </button>
        )}
      </div>
    </div>
  );
}

/* =========================================================================
   Audits
   ========================================================================= */

function AuditsTab({
  client,
  reload,
  onOpen,
}: {
  client: AmClientDetail;
  reload: () => void;
  onOpen: (auditId: string) => void;
}) {
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function remove(id: string) {
    try {
      await api.del(`/am/audits/${id}`);
      reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not delete the audit');
    }
  }

  return (
    <section className="card">
      <div className="card-head">
        <div className="grow">
          <span className="eyebrow">Environment</span>
          <h2>Audits</h2>
          <p className="sub">
            Findings are rated as they are made; the rating is what turns an audit into a plan.
          </p>
        </div>
        <button type="button" className="btn btn-primary btn-sm" onClick={() => setCreating(true)}>
          New audit
        </button>
      </div>

      {error && (
        <div className="card-body">
          <ErrorBanner message={error} />
        </div>
      )}

      {client.audits.length === 0 ? (
        <div className="card-body">
          <Empty title="Nothing assessed">
            <p>Run an environment audit against one of the question templates to get started.</p>
          </Empty>
        </div>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Audit</th>
                <th style={{ width: 200 }}>Template</th>
                <th style={{ width: 110 }}>Date</th>
                <th className="num" style={{ width: 100 }}>Answered</th>
                <th style={{ width: 110 }}>Status</th>
                <th style={{ width: 200 }}>Risk</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {client.audits.map((a) => (
                <tr key={a.AmAuditId}>
                  <td>
                    <div className="strong">{a.Name ?? 'Audit'}</div>
                    {a.Auditor && <div className="xs muted">{a.Auditor}</div>}
                  </td>
                  <td className="xs">{a.SetName ?? '—'}</td>
                  <td className="mono xs">{formatDate(a.AuditDate)}</td>
                  <td className="num">
                    {a.AnswerCount}
                    <span className="xs muted"> / {a.QuestionCount}</span>
                  </td>
                  <td>
                    <StateBadge status={a.Status} />
                  </td>
                  <td>
                    <span className="row" style={{ gap: 4 }}>
                      {a.CriticalCount > 0 && (
                        <span className="badge badge-critical">{a.CriticalCount} critical</span>
                      )}
                      {a.HighCount > 0 && (
                        <span className="badge badge-serious">{a.HighCount} high</span>
                      )}
                      {a.MediumCount > 0 && (
                        <span className="badge badge-warning">{a.MediumCount} med</span>
                      )}
                      {a.CriticalCount + a.HighCount + a.MediumCount === 0 && (
                        <span className="xs subtle">no raised risks</span>
                      )}
                    </span>
                  </td>
                  <td className="actions">
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => onOpen(a.AmAuditId)}
                    >
                      Open
                    </button>
                    <DeleteButton
                      what={`${a.Name ?? 'this audit'} and its answers (its recommendations are kept)`}
                      onConfirm={() => void remove(a.AmAuditId)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {creating && (
        <NewAuditModal
          clientId={client.AmClientId}
          onClose={() => setCreating(false)}
          onCreated={(auditId) => {
            setCreating(false);
            reload();
            onOpen(auditId);
          }}
        />
      )}
    </section>
  );
}

function NewAuditModal({
  clientId,
  onClose,
  onCreated,
}: {
  clientId: string;
  onClose: () => void;
  onCreated: (auditId: string) => void;
}) {
  const sets = useResource<AmQuestionSet[]>('/am/questions');
  const [template, setTemplate] = useState<string>('');
  const [name, setName] = useState('');
  const [auditor, setAuditor] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /* An audit that was answered somewhere else: the file is read and matched
     against the chosen template BEFORE anything is created, so a file that
     belongs to a different template is caught while it can still be swapped
     rather than leaving an empty audit behind. */
  const [file, setFile] = useState<File | null>(null);
  const [matched, setMatched] = useState<{ matches: Match[]; unmatched: string[] } | null>(null);
  const [reading, setReading] = useState(false);

  const options = (sets.data ?? []).filter((s) => s.Questions > 0);
  const chosen = template || options.find((s) => s.IsDefault)?.SetName || options[0]?.SetName || '';
  const answersReady = matched?.matches.filter((m) => m.changed).length ?? 0;

  /* Re-matched whenever the file or the template changes — picking a
     different template with a file already chosen must not keep showing the
     old template's count. */
  useEffect(() => {
    let live = true;
    if (!file || !chosen) {
      setMatched(null);
      return () => {
        live = false;
      };
    }
    setReading(true);
    setError(null);
    void (async () => {
      try {
        const [sheet, questions] = await Promise.all([
          readSpreadsheet(file),
          api.get<AmQuestion[]>(`/am/questions/${encodeURIComponent(chosen)}`),
        ]);
        if (!live) return;
        setMatched(matchAuditSheet(sheet, questions));
      } catch (err) {
        if (!live) return;
        setMatched(null);
        setError(err instanceof Error ? err.message : 'Could not read that file');
      } finally {
        if (live) setReading(false);
      }
    })();
    return () => {
      live = false;
    };
  }, [file, chosen]);

  async function submit() {
    setSaving(true);
    setError(null);
    try {
      const created = await api.post<{ AmAuditId: string }>(`/am/clients/${clientId}/audits`, {
        setName: chosen,
        name: name.trim(),
        auditor,
      });
      /* The answers go on straight away rather than as drafts: there is no
         editor open to review them in yet, and the audit opens on them the
         moment this returns. */
      const answers = matched?.matches.filter((m) => m.changed) ?? [];
      if (answers.length > 0) {
        await api.put(`/am/audits/${created.AmAuditId}/answers`, {
          answers: answers.map((m) => ({
            questionId: m.questionId,
            response: m.draft.response,
            risk: m.draft.risk,
            notes: m.draft.notes,
          })),
        });
      }
      onCreated(created.AmAuditId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create the audit');
      setSaving(false);
    }
  }

  return (
    <Modal
      title="New audit"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={saving || !chosen}
            onClick={() => void submit()}
          >
            {saving
              ? 'Creating…'
              : answersReady > 0
                ? `Create with ${answersReady} answer${answersReady === 1 ? '' : 's'}`
                : 'Create and start'}
          </button>
        </>
      }
    >
      {error && <ErrorBanner message={error} />}
      {sets.loading ? (
        <Loading label="Loading templates" />
      ) : options.length === 0 ? (
        <p className="small muted">
          No question template has any questions in it yet. Build one on the audit templates tab
          first.
        </p>
      ) : (
        <>
          <Field label="Template" hint="The audit is prefilled with every question in it.">
            <select className="select" value={chosen} onChange={(e) => setTemplate(e.target.value)}>
              {options.map((s) => (
                <option key={s.SetName} value={s.SetName}>
                  {s.SetName} ({s.Questions} questions)
                </option>
              ))}
            </select>
          </Field>
          <Field label="Audit name" hint="Defaults to the template name.">
            <input
              className="input"
              placeholder={`${chosen} — Q3`}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </Field>
          <Field label="Auditor">
            <input className="input" value={auditor} onChange={(e) => setAuditor(e.target.value)} />
          </Field>

          {/* Already answered elsewhere. Optional — an audit started here and
              filled in here never touches this. */}
          <Field
            label="Answers from a spreadsheet"
            hint="Optional. An audit exported from here, filled in and sent back. .csv or .xlsx."
          >
            <div className="row wrap" style={{ gap: 'var(--space-3)' }}>
              <label className="btn btn-secondary btn-sm">
                {file ? 'Choose a different file' : 'Import audit'}
                <input
                  type="file"
                  accept=".csv,.xlsx"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    setFile(e.target.files?.[0] ?? null);
                    e.target.value = '';
                  }}
                />
              </label>
              {file && (
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => setFile(null)}
                >
                  Remove
                </button>
              )}
              {reading && <span className="xs muted">Reading…</span>}
            </div>
          </Field>

          {file && matched && !reading && (
            <p className="xs muted">
              <strong>{file.name}</strong> — {answersReady} of the template&rsquo;s questions will
              be answered
              {matched.unmatched.length > 0
                ? `, ${matched.unmatched.length} row${
                    matched.unmatched.length === 1 ? ' matches' : 's match'
                  } nothing in “${chosen}”`
                : ''}
              .
              {answersReady === 0 &&
                ' Nothing in that file matches this template — check the template above is the one it was exported from.'}
            </p>
          )}
        </>
      )}
    </Modal>
  );
}

/* =========================================================================
   Recommendations
   ========================================================================= */

function RecommendationsTab({
  client,
  reload,
  onProposal,
}: {
  client: AmClientDetail;
  reload: () => void;
  onProposal: () => void;
}) {
  const [form, setForm] = useState<Partial<AmRecommendation> | null>(null);
  const [error, setError] = useState<string | null>(null);

  const sorted = [...client.recommendations].sort(
    (a, b) => priorityRank(topPriority(b)) - priorityRank(topPriority(a)),
  );
  const hours = sorted.reduce((sum, r) => sum + (r.Hours ?? 0), 0);

  async function setStatus(rec: AmRecommendation, status: string) {
    try {
      await api.put(`/am/recommendations/${rec.AmRecommendationId}`, { status });
      reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not update the status');
    }
  }

  async function remove(id: string) {
    try {
      await api.del(`/am/recommendations/${id}`);
      reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not delete the recommendation');
    }
  }

  return (
    <>
      <BucketTiles recs={sorted} hours={hours} />

      <section className="card">
        <div className="card-head">
          <div className="grow">
            <span className="eyebrow">What it turned into</span>
            <h2>Recommendations</h2>
          </div>
          {sorted.length > 0 && (
            <button type="button" className="btn btn-secondary btn-sm" onClick={onProposal}>
              Build a proposal
            </button>
          )}
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={() => setForm({ CostLines: [blankCostLine()], Status: 'Proposed' })}
          >
            Add one
          </button>
        </div>

        {error && (
          <div className="card-body">
            <ErrorBanner message={error} />
          </div>
        )}

        {sorted.length === 0 ? (
          <div className="card-body">
            <Empty title="Nothing recommended">
              <p>Raise them from audit findings, or add one directly.</p>
            </Empty>
          </div>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Recommendation</th>
                  <th style={{ width: 100 }}>Priority</th>
                  <th className="num" style={{ width: 130 }}>Price</th>
                  <th className="num" style={{ width: 80 }}>Hours</th>
                  <th style={{ width: 150 }}>Status</th>
                  {/* Ticked into the Budget tab's five-year plan, in the year chosen. */}
                  <th style={{ width: 150 }}>Budget</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {sorted.map((r) => (
                  <tr key={r.AmRecommendationId}>
                    <td>
                      <div className="strong">{r.Title}</div>
                      {r.Finding && <div className="xs muted">Finding: {r.Finding}</div>}
                      {r.Effort && <div className="xs muted">Effort: {r.Effort}</div>}
                    </td>
                    <td>
                      <PriorityBadge priority={topPriority(r)} />
                    </td>
                    <td className="num">
                      <CostCell rec={r} />
                    </td>
                    <td className="num">{r.Hours ? r.Hours.toFixed(1) : '—'}</td>
                    <td>
                      <select
                        className="select"
                        value={r.Status ?? 'Proposed'}
                        onChange={(e) => void setStatus(r, e.target.value)}
                        aria-label="Status"
                      >
                        {REC_STATUSES.map((s) => (
                          <option key={s}>{s}</option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <BudgetYearPicker rec={r} onSaved={reload} />
                    </td>
                    <td className="actions">
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={() => setForm({ ...r, CostLines: costLinesOf(r) })}
                      >
                        Edit
                      </button>
                      <DeleteButton
                        what={r.Title ?? 'this recommendation'}
                        onConfirm={() => void remove(r.AmRecommendationId)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {form && (
        <RecommendationModal
          clientId={client.AmClientId}
          rec={form}
          onClose={() => setForm(null)}
          onSaved={() => {
            setForm(null);
            reload();
          }}
        />
      )}
    </>
  );
}

/**
 * Taking a client off the books.
 *
 * Shows what will go quiet BEFORE it goes quiet. Deactivating is reversible,
 * but "reversible" is only reassuring if you can see what it is about to do —
 * and the answer is different for every client: one has three live compliance
 * assessments and an onboarding half-finished, the next has nothing.
 */
function DeactivateClientModal({
  clientId,
  name,
  onClose,
  onDone,
}: {
  clientId: string;
  name: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const preview = useResource<DeactivationChange[]>(
    `/am/clients/${clientId}/deactivation-preview`,
  );
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const changes = preview.data ?? [];
  const byTable = new Map<string, number>();
  for (const change of changes) byTable.set(change.table, (byTable.get(change.table) ?? 0) + 1);

  const LABELS: Record<string, string> = {
    'coo.SalesAccount': 'sales account marked inactive',
    'coo.ComplianceAssessment': 'compliance assessment archived',
    'coo.Onboarding': 'onboarding cancelled',
    'coo.AmClient': 'account management record marked inactive',
  };

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.post(`/am/clients/${clientId}/deactivate`, { reason: reason.trim() || null });
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not deactivate the client');
      setSaving(false);
    }
  };

  return (
    <Modal
      title={`Deactivate ${name}?`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={saving}
            onClick={() => void submit()}
          >
            {saving ? 'Deactivating…' : 'Deactivate'}
          </button>
        </>
      }
    >
      {error && <ErrorBanner message={error} />}

      <p className="small">
        They come off every list in the app — the client list, the compliance subject picker, the
        product matrix, the onboarding board. Nothing is deleted, and an administrator can put them
        back.
      </p>

      <p className="eyebrow mt-4">What goes quiet</p>
      {preview.loading ? (
        <Loading label="Checking" />
      ) : changes.length === 0 ? (
        <p className="small muted mt-2">
          Nothing else is attached to this client — only the company record itself changes.
        </p>
      ) : (
        <ul className="small mt-2">
          {[...byTable.entries()].map(([table, count]) => (
            <li key={table}>
              {count} × {LABELS[table] ?? table}
            </li>
          ))}
        </ul>
      )}

      <Field
        label="Why"
        hint="Kept with the record, so whoever reads it in a year knows what happened."
      >
        <textarea
          className="textarea"
          rows={2}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. contract ended 31 March, moved in-house"
        />
      </Field>
    </Modal>
  );
}

function RecommendationModal({
  clientId,
  rec,
  onClose,
  onSaved,
}: {
  clientId: string;
  rec: Partial<AmRecommendation>;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState(rec);
  const [lines, setLines] = useState<AmCostLine[]>(rec.CostLines ?? [blankCostLine()]);
  /* Priced when they were attached, so an existing recommendation shows what
     it quoted rather than what the catalogue says today. */
  const [products, setProducts] = useState<PickedProduct[]>(
    (rec.Products ?? []).map((p) => ({
      productId: p.ProductId,
      quantity: p.Quantity,
      name: p.Name,
      unitPrice: p.UnitPrice,
      periodType: p.PeriodType,
      vendor: p.Vendor,
    })),
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /* Templates — migration 190. Offered only when adding: an existing
     recommendation has already been written. */
  const isNew = !form.AmRecommendationId;
  const templates = useResource<AmRecommendationTemplate[]>(isNew ? TEMPLATES_PATH : null);
  const [templateId, setTemplateId] = useState('');
  const [saveTemplate, setSaveTemplate] = useState(false);
  const [templateName, setTemplateName] = useState('');

  /* Fills the form from a template. Products come at today's catalogue
     price; the status stays whatever the form says. */
  function applyTemplate(id: string) {
    setTemplateId(id);
    const t = templates.data?.find((x) => String(x.AmRecommendationTemplateId) === id);
    if (!t) {
      /* Custom: back to the blank form it opened with, keeping the status. */
      setForm((f) => ({ ...rec, Status: f.Status }));
      setLines(rec.CostLines ?? [blankCostLine()]);
      setProducts([]);
      return;
    }
    setForm((f) => ({
      ...f,
      Title: t.Title,
      Finding: t.Finding ?? '',
      Description: t.Description ?? '',
      Effort: t.Effort ?? '',
      Hours: t.Hours ?? 0,
    }));
    setLines(templateLines(t));
    setProducts(templateProducts(t));
  }

  async function submit() {
    setSaving(true);
    setError(null);
    const body = {
      title: (form.Title ?? '').trim(),
      finding: form.Finding ?? '',
      description: form.Description ?? '',
      costLines: lines,
      products: products.map((p) => ({ productId: p.productId, quantity: p.quantity })),
      effort: form.Effort ?? '',
      hours: form.Hours ?? 0,
      status: form.Status ?? 'Proposed',
    };
    try {
      if (form.AmRecommendationId) {
        await api.put(`/am/recommendations/${form.AmRecommendationId}`, body);
      } else {
        await api.post('/am/recommendations', { ...body, clientId });
      }
      /* After the recommendation, so a template that fails to save never costs
         the recommendation itself. Custom (typed-in) products are not in the
         catalogue and are left off the template. */
      /* New or existing: a recommendation already written for one client is
         as good a template as one written fresh. */
      if (saveTemplate) {
        try {
          await api.post(TEMPLATES_PATH, {
            name: (templateName.trim() || body.title).slice(0, 200),
            title: body.title,
            finding: body.finding,
            description: body.description,
            costLines: body.costLines,
            products: products.filter((p) => p.productId > 0).map((p) => ({ productId: p.productId, quantity: p.quantity })),
            effort: body.effort,
            hours: body.hours,
          });
        } catch (err) {
          setError(
            `The recommendation was saved, but the template was not: ${err instanceof Error ? err.message : 'unknown error'}`,
          );
          setSaving(false);
          return;
        }
      }
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save');
      setSaving(false);
    }
  }

  return (
    <Modal
      title={form.AmRecommendationId ? 'Edit recommendation' : 'Add recommendation'}
      onClose={onClose}
      size="xl"
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={saving || !(form.Title ?? '').trim()}
            onClick={() => void submit()}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      {error && <ErrorBanner message={error} />}
      {/* Always offered when adding, Custom first: a list that only appeared
          once a template existed was a list nobody knew was there. */}
      {isNew && (
        <Field
          label="Template"
          hint={
            templates.loading
              ? 'Loading the saved templates…'
              : templates.error
                ? `Could not load the saved templates: ${templates.error}`
                : (templates.data?.length ?? 0) === 0
                ? 'No templates saved yet — tick “Also save as a template” below to make one.'
                : 'Custom starts blank. A template fills the form below; change anything before saving.'
          }
        >
          <select className="select" value={templateId} onChange={(e) => applyTemplate(e.target.value)}>
            <option value="">Custom</option>
            {(templates.data ?? []).map((t) => (
              <option key={t.AmRecommendationTemplateId} value={t.AmRecommendationTemplateId}>
                {t.Name}
              </option>
            ))}
          </select>
        </Field>
      )}
      <Field label="Title">
        <input
          className="input"
          value={form.Title ?? ''}
          onChange={(e) => setForm({ ...form, Title: e.target.value })}
        />
      </Field>
      <Field label="Finding">
        <textarea
          className="textarea"
          rows={2}
          value={form.Finding ?? ''}
          onChange={(e) => setForm({ ...form, Finding: e.target.value })}
        />
      </Field>
      <Field label="Description and scope">
        <textarea
          className="textarea"
          rows={3}
          value={form.Description ?? ''}
          onChange={(e) => setForm({ ...form, Description: e.target.value })}
        />
      </Field>
      <AmProductPicker
        items={products}
        onChange={setProducts}
        onApplyAsCost={(items) =>
          setLines(costLinesFromProducts(items, lines[0]?.priority ?? 'Medium'))
        }
      />
      <CostLinesEditor lines={lines} onChange={setLines} />
      <div className="form-grid">
        <Field label="Effort or timeline">
          <input
            className="input"
            value={form.Effort ?? ''}
            onChange={(e) => setForm({ ...form, Effort: e.target.value })}
          />
        </Field>
        <Field label="Estimated hours">
          <input
            className="input"
            type="number"
            min="0"
            step="0.25"
            value={form.Hours ?? 0}
            onChange={(e) => setForm({ ...form, Hours: Number(e.target.value) || 0 })}
          />
        </Field>
        <Field label="Status">
          <select
            className="select"
            value={form.Status ?? 'Proposed'}
            onChange={(e) => setForm({ ...form, Status: e.target.value })}
          >
            {REC_STATUSES.map((s) => (
              <option key={s}>{s}</option>
            ))}
          </select>
        </Field>
      </div>
      <div className="stack mt-2" style={{ gap: 'var(--space-2)' }}>
        <label className="checkbox">
          <input type="checkbox" checked={saveTemplate} onChange={(e) => setSaveTemplate(e.target.checked)} />
          Also save as a template, to start other recommendations from
        </label>
        {saveTemplate && (
          <Field label="Template name" hint="How it is listed in the Template dropdown. Leave blank to use the title.">
            <input
              className="input"
              value={templateName}
              placeholder={(form.Title ?? '').trim() || 'e.g. MFA rollout — up to 50 users'}
              onChange={(e) => setTemplateName(e.target.value)}
            />
          </Field>
        )}
      </div>
    </Modal>
  );
}

/* =========================================================================
   Editing the client
   ========================================================================= */

function EditClientModal({
  client,
  onClose,
  onSaved,
}: {
  client: AmClientDetail;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState(client);
  /* Custom fields edit as an ordered key/value list. They came from a
     spreadsheet, so the key IS the label and both have to be editable. */
  const [custom, setCustom] = useState(
    Object.entries(client.CustomFields).map(([k, v]) => ({ k, v })),
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = <K extends keyof AmClientDetail>(key: K, value: AmClientDetail[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  async function submit() {
    setSaving(true);
    setError(null);
    try {
      await api.put(`/am/clients/${client.AmClientId}`, {
        name: form.Name.trim(),
        industry: form.Industry ?? '',
        website: form.Website ?? '',
        accountManagerEmployeeId: form.AccountManagerEmployeeId ?? null,
        primaryContact: form.PrimaryContact ?? '',
        email: form.Email ?? '',
        phone: form.Phone ?? '',
        address: form.Address ?? '',
        status: form.Status ?? 'Client',
        monthlyRevenue: Number(form.MonthlyRevenue) || 0,
        contractType: form.ContractType ?? '',
        notes: form.Notes ?? '',
        customFields: Object.fromEntries(
          custom.filter((r) => r.k.trim()).map((r) => [r.k.trim(), r.v]),
        ),
      });
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the client');
      setSaving(false);
    }
  }

  return (
    <Modal
      title="Edit client"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={saving || !form.Name.trim()}
            onClick={() => void submit()}
          >
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </>
      }
    >
      {error && <ErrorBanner message={error} />}
      <div className="form-grid">
        <Field label="Client name">
          <input className="input" value={form.Name} onChange={(e) => set('Name', e.target.value)} />
        </Field>
        <Field label="Industry">
          <input
            className="input"
            value={form.Industry ?? ''}
            onChange={(e) => set('Industry', e.target.value)}
          />
        </Field>
        <Field label="Account manager">
          <AccountManagerPicker
            value={form.AccountManagerEmployeeId}
            currentName={form.AccountManager}
            onChange={(next) => set('AccountManagerEmployeeId', next)}
          />
        </Field>
        <Field label="Primary contact">
          <input
            className="input"
            value={form.PrimaryContact ?? ''}
            onChange={(e) => set('PrimaryContact', e.target.value)}
          />
        </Field>
        <Field label="Email">
          <input
            className="input"
            type="email"
            value={form.Email ?? ''}
            onChange={(e) => set('Email', e.target.value)}
          />
        </Field>
        <Field label="Phone">
          <input
            className="input"
            value={form.Phone ?? ''}
            onChange={(e) => set('Phone', e.target.value)}
          />
        </Field>
        <Field label="Status">
          <select
            className="select"
            value={form.Status ?? 'Client'}
            onChange={(e) => set('Status', e.target.value)}
          >
            {CLIENT_STATUSES.map((s) => (
              <option key={s}>{s}</option>
            ))}
          </select>
        </Field>
        <Field label="Monthly recurring revenue">
          <input
            className="input"
            type="number"
            min="0"
            value={form.MonthlyRevenue ?? 0}
            onChange={(e) => set('MonthlyRevenue', Number(e.target.value) || 0)}
          />
        </Field>
        {/* No health score here: it is calculated every night, and set by
            hand only as an override with a reason, on the Health tab. */}
        <Field label="Contract type">
          <input
            className="input"
            value={form.ContractType ?? ''}
            onChange={(e) => set('ContractType', e.target.value)}
          />
        </Field>
        <Field label="Website">
          <input
            className="input"
            value={form.Website ?? ''}
            onChange={(e) => set('Website', e.target.value)}
          />
        </Field>
        <Field label="Address">
          <input
            className="input"
            value={form.Address ?? ''}
            onChange={(e) => set('Address', e.target.value)}
          />
        </Field>
      </div>

      <Field label="Notes">
        <textarea
          className="textarea"
          rows={3}
          value={form.Notes ?? ''}
          onChange={(e) => set('Notes', e.target.value)}
        />
      </Field>

      <Field label="Custom fields" hint="Columns from an imported client spreadsheet land here.">
        {custom.length === 0 && <p className="xs muted">None yet.</p>}
        {custom.map((row, i) => (
          <div key={i} className="row mb-4" style={{ marginBottom: 'var(--space-2)' }}>
            <input
              className="input"
              placeholder="Field name"
              value={row.k}
              onChange={(e) =>
                setCustom(custom.map((r, j) => (j === i ? { ...r, k: e.target.value } : r)))
              }
              style={{ flex: 1 }}
            />
            <input
              className="input"
              placeholder="Value"
              value={row.v}
              onChange={(e) =>
                setCustom(custom.map((r, j) => (j === i ? { ...r, v: e.target.value } : r)))
              }
              style={{ flex: 2 }}
            />
            <button
              type="button"
              className="btn btn-ghost btn-icon btn-sm"
              onClick={() => setCustom(custom.filter((_, j) => j !== i))}
              aria-label="Remove field"
            >
              ✕
            </button>
          </div>
        ))}
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={() => setCustom([...custom, { k: '', v: '' }])}
        >
          + Add field
        </button>
      </Field>
    </Modal>
  );
}


/**
 * One booked review, as it reads on the account card.
 *
 * The same shape whether it is overdue or ahead — a date, what it is, who is
 * in it and what it is carrying — because the difference that matters is the
 * badge, not the layout. Reusing the layout is also what lets the overdue ones
 * sit directly above the upcoming ones and still scan as one list.
 */
function ReviewLine({ sbr, overdue = false }: { sbr: AmSbr; overdue?: boolean }) {
  const attending = parseIdList(sbr.StakeholderIds).length;
  const audits = parseIdList(sbr.AuditIds).length;
  const recs = parseIdList(sbr.RecIds).length;

  return (
    <div className="am-review-line">
      <div className="row-between wrap">
        <span className="row">
          <span className="mono small">{formatDate(sbr.NextDate)}</span>
          {overdue && <span className="badge badge-critical">Past due</span>}
        </span>
        <span className="row">
          <span className="strong small">{sbr.Title ?? 'Review'}</span>
          {sbr.Cadence && <span className="badge badge-neutral">{sbr.Cadence}</span>}
        </span>
      </div>
      {sbr.Agenda && <p className="xs muted mt-2">{sbr.Agenda}</p>}
      {/* Zeroes are dropped rather than printed. "0 audits · 0 recommendations"
          on a review somebody has only just booked reads as a warning about
          nothing. */}
      {(attending > 0 || audits > 0 || recs > 0) && (
        <p className="xs muted mt-2">
          {[
            attending > 0 && `${attending} attending`,
            audits > 0 && `${audits} ${audits === 1 ? 'audit' : 'audits'}`,
            recs > 0 && `${recs} ${recs === 1 ? 'recommendation' : 'recommendations'}`,
          ]
            .filter(Boolean)
            .join(' · ')}
        </p>
      )}
    </div>
  );
}

interface AutotaskLink {
  linked: boolean;
  companyId: number | null;
  companyName: string | null;
  auto: boolean;
}

/** The client's Autotask Account Classification, already turned into its
 *  picklist label by the API. Null when the client has no Autotask company
 *  linked, or has one with no classification set. */
interface AutotaskClassification {
  linked: boolean;
  classification: string | null;
}

/**
 * Which Autotask company the Technical Support slides read this client's
 * tickets from. Nothing to pick: it is the client the review is for, found in
 * Autotask by name (or linked already, when the client was imported from
 * Autotask). See resolveCompany in routes/amAutotask.ts.
 */
function AutotaskCompanyLink({ clientId }: { clientId: string }) {
  const link = useResource<AutotaskLink>(`/am/clients/${clientId}/autotask`);
  if (link.loading) return <p className="xs muted">Finding this client in Autotask…</p>;
  if (link.error) return <p className="xs" style={{ color: 'var(--status-danger)' }}>{link.error}</p>;
  const d = link.data;
  if (!d) return null;
  return d.linked ? (
    <p className="small">
      Tickets come from <strong>{d.companyName}</strong> in Autotask.
    </p>
  ) : (
    <p className="small muted">
      This client could not be found in Autotask under its name here, so the ticket slides will be empty. Importing it
      from Autotask (Account Management › Import from Autotask) matches the names.
    </p>
  );
}

/**
 * A heading on the review form for the slide the fields under it fill: the
 * slide's place in the deck, its title, and a picture of that slide from the
 * CMIT template — with a thick rule above, so where one slide's fields end and
 * the next begin is obvious. Slide numbers are the full deck's, every optional
 * slide shown.
 */
function SlideHeading({ slide, title, thumbs = [] }: { slide: string; title: string; thumbs?: string[] }) {
  const [open, setOpen] = useState<string | null>(null);
  return (
    <div className="rv-form-slide">
      <div className="rv-form-slide-text">
        <span className="rv-form-slide-no">{slide}</span>
        <span className="rv-form-slide-title">{title}</span>
      </div>
      {thumbs.length > 0 && (
        <div className="rv-form-slide-thumbs">
          {thumbs.map((t) => (
            <button key={t} type="button" className="rv-form-slide-thumb" onClick={() => setOpen(t)} title="See the slide larger">
              <img src={t} alt={`What the ${title} slide looks like`} />
            </button>
          ))}
        </div>
      )}
      {open && (
        <div className="rv-form-slide-zoom" role="dialog" aria-label={`${title} slide`} onClick={() => setOpen(null)}>
          <img src={open} alt={`What the ${title} slide looks like`} />
        </div>
      )}
    </div>
  );
}
